/**
 * SyncManager.js - Core synchronization engine
 *
 * This is the core engine that handles all synchronization operations
 * including queue processing, inspection sync, and image compression.
 *
 * All operations run on the main thread with yield points to prevent UI blocking.
 */

import { db } from "@/services/db";
import convert from "@/services/convert";
import module_parser from "@/services/module_parser";
import store from "@/store";
import { Network } from "@capacitor/network";
import imageUploadService from "@/services/imageUploadService";
import documentUploadService from "@/services/documentUploadService";
import uuid from "@/plugins/uuid";
import eventBus from "@/services/eventBus";

// Image upload_status values that indicate the image only exists locally and has
// not yet been confirmed by the server. These images must never be deleted during
// an inspection sync, because the server has no record of them yet.
// 'uploaded' = bytes uploaded to S3 but the confirm call has not yet been
// acknowledged in the sync response — local row is still the source of truth.
// NOTE: this list governs deletion protection only. rescueOrphanedImages uses a
// narrower subset for re-queueing (see comment there).
const UNSYNCED_IMAGE_STATUSES = ['pending', 'uploading', 'uploaded', 'failed', 'cors_error', 'retrying', 'policy_error'];

// Returns true when an image row is unsynced (only exists locally) and must be
// protected from deletion. Treats missing/null upload_status as unsynced to
// avoid silently deleting older rows that predate the status field.
function isImageUnsynced(item) {
    return !item.upload_status || UNSYNCED_IMAGE_STATUSES.includes(item.upload_status);
}

// Document upload_status values that indicate the document only exists locally
// and has not yet been confirmed by the server (same upload lifecycle as images,
// see documentUploadService). A freshly created document is uploaded to S3 and
// only becomes a real server record once its confirm call is acknowledged in a
// subsequent sync response — until then the local row is the only copy and must
// never be deleted during an inspection sync.
const UNSYNCED_DOCUMENT_STATUSES = ['pending', 'uploading', 'uploaded', 'failed', 'cors_error', 'retrying', 'policy_error'];

// Returns true when a document row is unsynced (only exists locally) and must be
// protected from deletion. Unlike images, documents pulled from the backend are
// NOT backfilled with an upload_status, so a missing/null status means "came from
// the server" (safe to delete when the server drops it) — only an explicit
// in-flight status protects the row, otherwise server-side deletes would never
// propagate to the local database.
function isDocumentUnsynced(item) {
    return UNSYNCED_DOCUMENT_STATUSES.includes(item.upload_status);
}

// Returns true when a record was created locally and its POST has not yet been
// accepted by the server (patch_post_parser sets not_synced=true on creation).
// Such records exist ONLY locally, so an inspection pull must never delete them
// just because the server response doesn't contain them — the POST job may still
// be queued or sitting in failed_jobs awaiting retry. Once the POST succeeds the
// server's copy (without the flag) overwrites the local row and the protection
// ends, so server-side deletes propagate normally for synced records.
function isRecordUnsynced(item) {
    return item.not_synced === true;
}

class SyncManager {
    constructor() {
        this.isRunning = false;
        this.currentTask = null;
        this.lastProgressUpdate = Date.now();
        this.callbacks = {};
        this.taskTimeoutId = null;
        this.platform = null;
        this.taskChunkSize = 100; // Default chunk size for batch operations
        this.maxAttempts = 3; // Maximum retry attempts for failed jobs

        // Rate limiting configuration
        this.requestDelay = 200; // Default delay between requests in ms
        this.rateLimitBackoff = {
            initialDelay: 1000, // Initial delay when 429 is encountered (1 second)
            maxDelay: 60000, // Maximum delay (60 seconds)
            multiplier: 2, // Exponential backoff multiplier
        };
        this.currentBackoffDelay = 0; // Current backoff delay
        this.rateLimitPauseUntil = 0; // Timestamp until which queue processing should be paused

        // Safety mechanism to prevent infinite loops
        this.processedJobIds = new Set(); // Track processed job IDs in current run
        this.maxJobProcessingAttempts = 5; // Max times to see the same job in one run

        // Cleanup concurrency control
        this._cleanupInProgress = false; // Prevent concurrent deleteFloatingRecords runs

        // Failed job timeout configuration
        this.maxFailedJobAge = 4; // Hours before moving to permanently failed (default: 4 hours)
        // 4 hours gives enough time for temporary issues to resolve while not keeping jobs stuck too long
    }

    /**
     * Initialize the synchronization system
     * @param {Object} config - Optional configuration overrides
     */
    async init(config = {}) {
        // Apply configuration overrides
        if (config.maxFailedJobAge !== undefined) {
            this.maxFailedJobAge = config.maxFailedJobAge;
            console.log(`Failed job timeout set to ${this.maxFailedJobAge} hours`);
        }
        if (config.maxAttempts !== undefined) {
            this.maxAttempts = config.maxAttempts;
        }
        if (config.requestDelay !== undefined) {
            this.requestDelay = config.requestDelay;
        }

        // Detect platform for platform-specific optimizations
        this.platform = (await import("@capacitor/core")).Capacitor.getPlatform();

        // Set appropriate chunk size based on platform
        if (this.platform === "ios") {
            this.taskChunkSize = 50; // Smaller chunks for iOS
        } else if (this.platform === "android") {
            this.taskChunkSize = 100; // Medium chunks for Android
        } else {
            this.taskChunkSize = 200; // Larger chunks for web
        }

        console.log(`Sync manager initialized for ${this.platform} platform`);

        // Ensure database is ready
        await this.ensureDatabaseReady();

        // Process any pending jobs at startup if online
        if (store.getters.online && !store.getters.isQueueRunning) {
            this.checkForPendingJobs();
        }
    }

    /**
     * Check for pending jobs at startup
     */
    async checkForPendingJobs() {
        try {
            const jobCount = await db.jobs.count();
            if (jobCount > 0) {
                console.log(`Found ${jobCount} pending jobs at startup, triggering queue processing`);
                // Wait a short time to allow app to fully initialize
                setTimeout(() => {
                    // Don't wait for this to complete - fire and forget
                    this.startTask("process_queue", {}, {}).catch((err) => {
                        console.error("Error starting initial queue processing:", err);
                    });
                }, 3000);
            }
        } catch (error) {
            console.error("Error checking for pending jobs:", error);
        }
    }

    /**
     * Ensure database is ready and schema is fully migrated
     */
    async ensureDatabaseReady() {
        try {
            if (!db.isOpen()) {
                console.log("Database is not open, attempting to open it");
                await db.open();
            }
            return true;
        } catch (error) {
            console.error("Error ensuring database is ready:", error);
            return false;
        }
    }

    /**
     * Start a task with progress tracking
     *
     * @param {string} taskType - Type of task to start (process_queue, sync_inspection, etc.)
     * @param {object} params - Parameters for the task
     * @param {object} callbacks - Callbacks for task events (progress, complete, error, etc.)
     * @returns {boolean} - Whether the task was started successfully
     */
    async startTask(taskType, params = {}, callbacks = {}) {
        // Initialize if needed
        if (!this.platform) {
            await this.init();
        }

        // Don't run tasks if no bearer token (except setup_base_tables)
        if (!store.getters.getBearer && ["process_queue", "sync_inspection"].includes(taskType)) {
            console.log(`${taskType} not started: not logged in yet`);
            if (callbacks.error) {
                callbacks.error(new Error("Not logged in"));
            }
            return false;
        }

        // Check network status for network-dependent tasks
        if (["process_queue", "sync_inspection"].includes(taskType)) {
            const isOffline = !store.getters.online;
            const isForceSync = params?.forceSync === true;
            const ignoreOffline = params?.ignoreOffline === true;

            // Regular network checks for normal operations
            if (isOffline && !ignoreOffline && !isForceSync) {
                const errorMessage = `Cannot start ${taskType} while offline`;
                console.log(errorMessage);
                return false;
            }
        }

        // Check if another task is already running
        if (this.isRunning) {
            // If forceRestart is set, stop the current task
            if (params.forceRestart === true) {
                console.log(`Force restarting ${taskType}, stopping current task: ${this.currentTask?.type}`);
                this.stopTask("User requested force restart");
            } else {
                // Don't wait, just fail if another task is running
                const errorMessage = `Cannot start ${taskType} while ${this.currentTask?.type} is running`;
                console.log(errorMessage);
                return false;
            }
        }

        // Register callbacks
        this.callbacks = callbacks || {};

        // Add connection & device info to task params
        const enhancedParams = {
            ...params,
            api_url: store.getters.getApiUrl,
            bearer: store.getters.getBearer,
            platform: this.platform,
            connectionType: store.getters.getConnectionType,
            taskChunkSize: this.taskChunkSize,
            online: store.getters.online,
        };

        // Initialize task tracking. The cancelToken is checked inside the task loops
        // so that stopTask()/timeout actually terminate the running work — before,
        // "stopping" only reset the bookkeeping while the async loop kept going,
        // racing against any newly started task.
        const cancelToken = { cancelled: false };
        const taskRecord = {
            type: taskType,
            params: enhancedParams,
            startTime: Date.now(),
            progress: params.resumeProgress || 0,
            cancelToken,
        };
        this.isRunning = true;
        this.currentTask = taskRecord;

        this.lastProgressUpdate = Date.now();

        // Set up task timeout
        this.setupTaskTimeout();

        // Create a progress updater function. Guarded so a cancelled task that is
        // still winding down can't reset the timeout or overwrite the progress of a
        // task that replaced it.
        const updateProgress = (progress, details) => {
            if (this.currentTask !== taskRecord) return;
            this.handleProgress(taskType, progress, details);
        };

        // Trigger 'start' callback
        this.triggerCallback("start", taskType, { params: enhancedParams });

        // Execute the appropriate task based on type
        try {
            if (taskType === "process_queue") {
                await this.processQueue(enhancedParams, updateProgress, cancelToken);
            } else if (taskType === "sync_inspection") {
                await this.syncInspection(enhancedParams, updateProgress, cancelToken);
            } else if (taskType === "setup_base_tables") {
                await this.setupBaseTables(enhancedParams, updateProgress, cancelToken);
            } else if (taskType === "compress_images") {
                await this.compressImages(enhancedParams, updateProgress, cancelToken);
            } else {
                throw new Error(`Unknown task type: ${taskType}`);
            }

            // If this task was stopped (or timed out) while awaiting, another task may
            // already be running with its own state and callbacks. Touching isRunning/
            // currentTask/the timeout here would clobber that task, and firing
            // "complete" would resolve the NEW task's promise with the OLD task's
            // result. The stop/timeout handler already fired the right callback.
            if (this.currentTask !== taskRecord) {
                console.log(`Task ${taskType} finished after being stopped, skipping completion handling`);
                return false;
            }

            // Calculate duration before nullifying currentTask
            const duration = Date.now() - taskRecord.startTime;

            // Task completed successfully
            this.isRunning = false;
            this.currentTask = null;

            // Clear the timeout
            clearTimeout(this.taskTimeoutId);

            // Trigger completion callback
            this.triggerCallback("complete", taskType, {
                duration: duration,
            });

            return true;
        } catch (error) {
            // Same staleness guard as the success path: a stopped task must not touch
            // the state or callbacks of whatever task replaced it.
            if (this.currentTask !== taskRecord) {
                console.log(`Task ${taskType} errored after being stopped, skipping error handling:`, error.message);
                return false;
            }

            // Calculate duration before nullifying currentTask
            const duration = Date.now() - taskRecord.startTime;

            // Task failed
            this.isRunning = false;
            this.currentTask = null;

            // Clear the timeout
            clearTimeout(this.taskTimeoutId);

            // Trigger error callback
            this.triggerCallback("error", taskType, {
                error: error.message,
                details: error.stack,
                duration: duration,
            });

            return false;
        }
    }

    /**
     * Process the job queue
     *
     * @param {object} params - Parameters for queue processing
     * @param {function} updateProgress - Function to update progress
     */
    async processQueue(params, updateProgress, cancelToken = null) {
        // Set initial progress
        updateProgress(0, "Starting queue processing");

        // Reset processed job tracking for this run
        this.processedJobIds.clear();

        try {
            // Check authentication - don't process if we're not logged in
            if (!params.bearer) {
                console.log("Queue skipped: not logged in yet");
                updateProgress(100, "Skipped - not logged in");
                return; // Silent success
            }

            // Check network status
            if (!params.online && !params.ignoreOffline) {
                throw new Error("Cannot process queue while offline");
            }

            // 1. First, send any permanently failed jobs to the server
            await this.runPermanentlyFailedJobs(params, cancelToken);
            updateProgress(10, "Processed permanently failed jobs");

            // 2. Process any existing failed jobs (retry up to max attempts)
            await this.runFailedJobs(params, cancelToken);
            updateProgress(20, "Processed failed jobs");

            // Rescue images that are stuck in pending/uploading/failed/retrying but have no
            // upload job in db.jobs (orphaned images). This re-creates the job so the upload
            // can proceed. Must run after runFailedJobs (which covers db.failed_jobs) and
            // before we load jobMetadata so the new jobs are included in this run.
            await this.rescueOrphanedImages();

            // 3. Get pending jobs - MEMORY OPTIMIZED
            // Load only lightweight metadata for sorting, then fetch full job data in batches
            console.log('[processQueue] Loading job metadata for sorting...');
            const jobMetadata = [];

            await db.jobs.each(job => {
                // Only collect fields needed for sorting and counting
                jobMetadata.push({
                    id: job.id,
                    type: job.type,
                    module: job.module,
                    // Keep uuid for fetching later
                    uuid: job.uuid
                });
            });

            console.log(`[processQueue] Found ${jobMetadata.length} jobs to process`);

            // Sort jobs based on priority (using lightweight metadata)
            const jobPriority = {
                post: 1,
                patch: 2,
                patcharray: 3,
                upload: 4,
                "upload-document": 5,
                delete: 6,
                "image-s3-upload": 8, // Create images first
                "image-s3-update": 9, // Update images after creates
                "image-confirm": 10,
            };

            // Module priority for proper dependency ordering
            // Lower numbers are processed first
            const modulePriority = {
                // Parent entities first
                inspections: 1,
                projects: 2,
                customers: 3,
                locations: 4,
                location_items: 5,

                // Components before their children
                location_item_components: 6,
                base_components: 6,

                // Dependent entities after parents
                measurements: 7,
                observations: 7,
                samples: 8,
                documents: 9,
                images: 10,

                // Form-related entities
                forms: 11,
                answers: 12,
                answers_inspections: 13,
                answers_location_item_components: 14,
                forms_location_item_components: 15,

                // System entities
                location_systems: 16,
                location_system_types: 17,

                // Other entities
                notifications: 18,
                scios_codes: 19,

                // Default for any unmapped modules
                default: 99,
            };

            const sortedJobMetadata = jobMetadata.sort((a, b) => {
                // First sort by job type priority
                const typeDiff = jobPriority[a.type.toLowerCase()] - jobPriority[b.type.toLowerCase()];
                if (typeDiff !== 0) return typeDiff;

                // For same type, sort by module priority to respect dependencies
                const aModulePriority = modulePriority[a.module] || modulePriority.default;
                const bModulePriority = modulePriority[b.module] || modulePriority.default;
                const moduleDiff = aModulePriority - bModulePriority;
                if (moduleDiff !== 0) return moduleDiff;

                // For same module, maintain original order (FIFO)
                return 0;
            });

            const totalJobs = sortedJobMetadata.length;
            if (totalJobs === 0) {
                updateProgress(100, "No jobs to process");
                return;
            }

            // Count image jobs for notification
            const imageJobsCount = sortedJobMetadata.filter(
                (job) =>
                    job.type === "image-s3-upload" || job.type === "image-s3-update" || job.type === "image-confirm"
            ).length;

            if (imageJobsCount >= 2) {
                // Emit event for sync notification
                eventBus.emit("image-sync-started", {
                    count: imageJobsCount,
                });
                console.log(`Emitted image-sync-started event for ${imageJobsCount} images`);
            }

            // Process each job - use chunks to avoid blocking UI
            let processedCount = 0;

            // Divide job metadata into chunks to improve UI responsiveness
            const chunks = this.chunkArray(sortedJobMetadata, this.taskChunkSize);

            for (let i = 0; i < chunks.length; i++) {
                const jobMetadataChunk = chunks[i];

                // Process each job in the chunk
                for (const jobMeta of jobMetadataChunk) {
                    // Terminate promptly when the task was stopped or timed out —
                    // unprocessed jobs simply stay queued for the next run.
                    if (cancelToken?.cancelled) {
                        console.log(`[processQueue] Task cancelled after ${processedCount}/${totalJobs} jobs, stopping`);
                        return;
                    }

                    /**
                     * DUPLICATE JOB DETECTION
                     *
                     * Why we delete instead of moving to failed_jobs:
                     *
                     * processedJobIds is a session-scoped Set (created fresh each processQueue() run).
                     * If a job ID is in this Set, it means we SUCCESSFULLY processed it in THIS run
                     * (failed jobs are never added to processedJobIds - they go to failed_jobs).
                     *
                     * Duplicate scenario:
                     * 1. Job processed successfully → backend accepts data
                     * 2. Job added to processedJobIds Set
                     * 3. Job deletion fails (DB lock, timing issue, etc.)
                     * 4. Job remains in jobs table
                     * 5. Loop continues → encounters same job again
                     * 6. processedJobIds.has(jobId) returns true
                     *
                     * Why deleting is safe:
                     * - Backend already has the data (step 1 succeeded)
                     * - Job can't be in processedJobIds unless it succeeded
                     * - This is NOT a genuine failure (moving to failed_jobs would be false positive)
                     *
                     * Alternative considered: Move to failed_jobs
                     * - Rejected: Creates false positives in backend logs
                     * - Confirmed: These were always false alarms in production
                     */
                    const jobIdStr = String(jobMeta.id);
                    if (this.processedJobIds.has(jobIdStr)) {
                        console.warn(
                            `[processQueue] Job ${jobMeta.id} already processed in this run, skipping to prevent loop. ` +
                            `This usually means the job was successfully processed but deletion failed.`
                        );

                        try {
                            await this.deleteJob({ id: jobMeta.id });
                            console.log(`[processQueue] Successfully deleted duplicate job ${jobMeta.id}`);
                        } catch (deleteError) {
                            console.error(`[processQueue] Failed to delete duplicate job ${jobMeta.id}:`, deleteError);
                        }

                        processedCount++;
                        continue;
                    }
                    this.processedJobIds.add(jobIdStr);

                    // Check if we should pause due to rate limiting
                    if (this.rateLimitPauseUntil > Date.now()) {
                        const waitTime = this.rateLimitPauseUntil - Date.now();
                        updateProgress(
                            20 + Math.floor(80 * (processedCount / totalJobs)),
                            `Rate limited - waiting ${Math.ceil(waitTime / 1000)}s`
                        );
                        await new Promise((resolve) => setTimeout(resolve, waitTime));

                        // The rate-limit pause can last up to a minute — re-check
                        // cancellation before resuming work.
                        if (cancelToken?.cancelled) {
                            console.log(`[processQueue] Task cancelled during rate-limit pause, stopping`);
                            return;
                        }
                    }

                    // MEMORY OPTIMIZATION: Fetch full job data only when processing
                    // This avoids keeping large payloads in memory
                    const job = await db.jobs.get(jobMeta.id);

                    if (!job) {
                        /**
                         * Job no longer exists in database
                         *
                         * This is expected behavior and can happen when:
                         * - Job was successfully processed and deleted, but metadata snapshot was stale
                         * - Rare edge case: Database corruption or external deletion
                         *
                         * We increment processedCount to maintain accurate progress reporting.
                         * Without this, progress would stop short (e.g., 95/100 instead of 100/100).
                         * The job has been "processed" by determining it no longer needs action.
                         */
                        console.log(`[processQueue] Job ${jobMeta.id} no longer exists, skipping`);
                        processedCount++;
                        continue;
                    }

                    try {
                        console.log(`Processing job ${job.id} of type ${job.type}`);
                        await this.processJob(job, params);
                        console.log(`Successfully processed job ${job.id}`);
                    } catch (error) {
                        console.error(`Job ${job.id} failed:`, {
                            type: job.type,
                            error: error.message,
                            stack: error.stack,
                        });
                        // Check if we're offline and this is a network error
                        if (error.isNetworkError && !params.online) {
                            console.log(`Job ${job.id} failed due to network error, stopping queue processing`);
                            // Put job back at the front of the queue
                            throw new Error("Network connection lost during queue processing");
                        }
                        await this.toFailedJobs(job, error);
                    }

                    processedCount++;

                    // Update progress
                    updateProgress(
                        20 + Math.floor(80 * (processedCount / totalJobs)),
                        `Processing job ${processedCount}/${totalJobs}: ${jobMeta.type}`
                    );

                    // Add delay between requests to avoid overwhelming the server
                    if (processedCount < totalJobs) {
                        await new Promise((resolve) => setTimeout(resolve, this.requestDelay));
                    }
                }

                // Yield to UI thread between chunks
                await new Promise((resolve) => setTimeout(resolve, 0));
            }

            // Clean up any floating records
            await this.deleteFloatingRecords();

            updateProgress(100, "Queue processing completed");
        } catch (error) {
            console.error("Error processing queue:", error);
            throw error;
        }
    }

    /**
     * Find images stuck in a pending-like state with no corresponding upload job and
     * create missing jobs so they are included in the current processQueue run.
     *
     * This covers scenarios where the app crashed between saving the image locally and
     * creating the job, or where the job was removed while the image record was not.
     */
    async rescueOrphanedImages() {
        try {
            // Only rescue statuses where reset-to-pending + re-upload is the correct
            // recovery. Excluded on purpose:
            //   'uploaded'     — bytes are already in S3 but the session_uuid needed
            //                    for the confirm call lives only in memory, so we
            //                    can't reconstruct an image-confirm job here. A new
            //                    sync will surface it (or the user can re-try).
            //   'cors_error'   — terminal client/policy failures; retrying without
            //   'policy_error'   user/admin intervention will fail the same way.
            // 'uploading' IS rescued: if the app crashed mid-upload and the in-memory
            // session is lost, a fresh upload is the only recovery — at worst this
            // costs one duplicate S3 PUT (which S3 deduplicates by key).
            // All seven statuses are still protected from deletion via
            // UNSYNCED_IMAGE_STATUSES — this list only controls re-queueing.
            const unsyncedImages = await db.images
                .where('upload_status')
                .anyOf(['pending', 'uploading', 'failed', 'retrying'])
                .toArray();

            if (unsyncedImages.length === 0) return 0;

            // Build sets of image UUIDs already tracked by active jobs or failed-jobs
            // so we don't create duplicate jobs.
            const activeJobs = await db.jobs.toArray();
            const trackedByActive = new Set(
                activeJobs
                    .filter(j => (j.type === 'image-s3-upload' || j.type === 'image-confirm') && j.payload?.image_uuid)
                    .map(j => j.payload.image_uuid)
            );

            const failedJobs = await db.failed_jobs.toArray();
            const trackedByFailed = new Set(
                failedJobs
                    .filter(j => (j.type === 'image-s3-upload' || j.type === 'image-confirm') && j.payload?.image_uuid)
                    .map(j => j.payload.image_uuid)
            );

            // Resolve entity info once per unique imageable_uuid to avoid an
            // N+1 walk over db.* when many orphans share an entity.
            const imagesToRescue = unsyncedImages.filter(
                (img) => !trackedByActive.has(img.uuid) && !trackedByFailed.has(img.uuid)
            );
            const uniqueEntityUuids = [...new Set(imagesToRescue.map((img) => img.imageable_uuid))];
            const entityInfoEntries = await Promise.all(
                uniqueEntityUuids.map(async (id) => [id, await imageUploadService.findEntityInfo(id)])
            );
            const entityInfoByUuid = new Map(entityInfoEntries);

            let rescued = 0;
            for (const image of imagesToRescue) {
                const entityInfo = entityInfoByUuid.get(image.imageable_uuid);
                if (!entityInfo) {
                    console.warn(`[rescueOrphanedImages] No local entity found for image ${image.uuid} (imageable_uuid: ${image.imageable_uuid}) — skipping`);
                    continue;
                }

                await db.images.update(image.uuid, { upload_status: 'pending', upload_error: null });
                await imageUploadService.queueForLaterUpload(image, entityInfo.type, image.imageable_uuid);
                rescued++;
                console.log(`[rescueOrphanedImages] Re-queued orphaned image ${image.uuid} (entity: ${entityInfo.type})`);
            }

            if (rescued > 0) {
                console.log(`[rescueOrphanedImages] Rescued ${rescued} orphaned image(s)`);
            }

            return rescued;
        } catch (error) {
            console.error('[rescueOrphanedImages] Error (non-fatal):', error);
            return 0;
        }
    }

    /**
     * Process a single job
     *
     * @param {object} job - The job to process
     * @param {object} params - Parameters for job processing
     */
    async processJob(job, params) {
        // Validate job structure - S3 upload jobs don't need URL
        const isS3Job = ["image-s3-upload", "image-s3-update", "image-confirm", "document-s3-upload", "document-s3-update", "document-confirm"].includes(job?.type);
        if (!job || !job.type || (!job.url && !isS3Job)) {
            console.error(`Invalid job structure:`, {
                id: job?.id,
                type: job?.type,
                url: job?.url,
                module: job?.module,
                payload: job?.payload,
            });
            await this.deleteJob(job);
            return;
        }

        // Check if job has exceeded max attempts
        const jobAttempt = parseInt(job.attempt || 0);
        const maxAttempts = isS3Job ? 3 : this.maxAttempts;
        if (jobAttempt >= maxAttempts) {
            console.error(
                `Job ${job.id} has already reached max attempts (${jobAttempt}/${maxAttempts}), moving to permanently failed`
            );
            const permanentJob = {
                ...job,
                error_message: `Exceeded max attempts (${maxAttempts})`,
                failed_at: new Date().toISOString(),
            };
            await db.permanently_failed_jobs.put(permanentJob);
            await this.deleteJob(job);
            return;
        }

        // Only process POST, PATCH, DELETE and upload jobs
        const validTypes = [
            "post",
            "patch",
            "patcharray",
            "delete",
            "upload",
            "upload-document",
            "image-s3-upload",
            "image-s3-update",
            "image-confirm",
            "document-s3-upload",
            "document-s3-update",
            "document-confirm",
        ];
        const jobType = job.type.toLowerCase();

        if (!validTypes.includes(jobType)) {
            console.warn(`Invalid job type: ${job.type}, removing from queue`);
            await this.deleteJob(job);
            return;
        }

        // Final URL validation - S3 upload jobs don't need URL
        if (!isS3Job && job.url && job.url.trim() === "") {
            console.error(`Empty URL for job ${job.id}, removing from queue`);
            await this.deleteJob(job);
            return;
        }

        // Determine HTTP method based on job.type
        let method;
        if (["upload", "upload-document"].includes(job.type)) {
            method = "POST";
        } else if (jobType === "patcharray") {
            method = "PATCH";
        } else {
            method = job.type.toUpperCase();
        }

        // Base options for fetch
        const options = {
            method,
            headers: {
                ...(params.bearer && { Authorization: `Bearer ${params.bearer}` }),
                Accept: "application/vnd.api+json",
            },
        };

        // Set body based on job.type
        if (["post", "patch", "patcharray"].includes(jobType)) {
            // Validate payload exists and is not empty
            if (!job.payload || (typeof job.payload === "object" && Object.keys(job.payload).length === 0)) {
                console.error(`Job ${job.id} (${job.type}) has empty or missing payload, removing from queue`);
                await this.deleteJob(job);
                return;
            }

            options.headers["Content-Type"] = "application/json";
            options.body = JSON.stringify(job.payload);
        } else if (["image-s3-upload", "image-s3-update", "image-confirm", "document-s3-upload", "document-s3-update", "document-confirm"].includes(job.type)) {
            // Handle new S3 upload job types - these are processed differently
            console.log(`Processing S3 upload job ${job.id}:`, {
                type: job.type,
                payload: job.payload,
            });

            try {
                if (job.type === "image-s3-upload") {
                    // Process S3 upload
                    if (!job.payload || !job.payload.image_uuid) {
                        throw new Error(`Invalid S3 upload job payload: ${JSON.stringify(job.payload)}`);
                    }

                    const image = await db.images.where("uuid").equals(job.payload.image_uuid).first();
                    if (!image) {
                        throw new Error(`Image not found in database: ${job.payload.image_uuid}`);
                    }

                    console.log(`Uploading image ${image.uuid} to S3 with bearer from params`);

                    // Initialize the service with bearer from params
                    imageUploadService.apiUrl = params.api_url;
                    imageUploadService.bearerToken = params.bearer;

                    console.log("Using bearer from params:", {
                        hasBearer: !!params.bearer,
                        bearerLength: params.bearer ? params.bearer.length : 0,
                    });

                    await imageUploadService.uploadImage(image, job.payload.entity_type, job.payload.imageable_uuid);

                    console.log(`Successfully uploaded image ${image.uuid}`);

                    // Job completed successfully
                    await this.deleteJob(job);
                    return;
                } else if (job.type === "image-s3-update") {
                    // Process S3 image update (replace existing image)
                    if (!job.payload || !job.payload.existing_image_uuid) {
                        throw new Error(`Invalid S3 update job payload: ${JSON.stringify(job.payload)}`);
                    }

                    console.log(`Processing image update for ${job.payload.existing_image_uuid}`, {
                        entityType: job.payload.entity_type,
                        entityUuid: job.payload.imageable_uuid,
                    });

                    // Initialize the service with bearer from params
                    imageUploadService.apiUrl = params.api_url;
                    imageUploadService.bearerToken = params.bearer;

                    await imageUploadService.updateImage(
                        job.payload.existing_image_uuid,
                        job.payload.new_image_data,
                        job.payload.entity_type,
                        job.payload.imageable_uuid
                    );

                    console.log(`Successfully updated image ${job.payload.existing_image_uuid}`);
                    // Job completed successfully
                    await this.deleteJob(job);
                    return;
                } else if (job.type === "image-confirm") {
                    // This is handled by uploadImage, but if we have a standalone confirm job
                    await imageUploadService.confirmUpload(
                        job.payload.upload_id,
                        job.payload.image_uuid,
                        job.payload.entity_type,
                        job.payload.entity_uuid,
                        job.payload.s3_key,
                        job.payload.title,
                        job.payload.orientation
                    );

                    await this.deleteJob(job);
                    return;
                } else if (job.type === "document-s3-upload") {
                    // Process document S3 upload
                    if (!job.payload || !job.payload.document_uuid) {
                        throw new Error(`Invalid document S3 upload job payload: ${JSON.stringify(job.payload)}`);
                    }

                    const document = await db.documents.where("uuid").equals(job.payload.document_uuid).first();
                    if (!document) {
                        throw new Error(`Document not found in database: ${job.payload.document_uuid}`);
                    }

                    console.log(`Uploading document ${document.uuid} to S3 with bearer from params`);

                    // Initialize the service with bearer from params
                    documentUploadService.apiUrl = params.api_url;
                    documentUploadService.bearerToken = params.bearer;

                    await documentUploadService.uploadDocument(document, job.payload.entity_type, job.payload.documentable_uuid);

                    console.log(`Successfully uploaded document ${document.uuid}`);

                    // Job completed successfully
                    await this.deleteJob(job);
                    return;
                } else if (job.type === "document-s3-update") {
                    // Process document S3 update (replace existing document)
                    if (!job.payload || !job.payload.existing_document_uuid) {
                        throw new Error(`Invalid document S3 update job payload: ${JSON.stringify(job.payload)}`);
                    }

                    console.log(`Processing document update for ${job.payload.existing_document_uuid}`, {
                        entityType: job.payload.entity_type,
                        entityUuid: job.payload.documentable_uuid,
                    });

                    // Initialize the service with bearer from params
                    documentUploadService.apiUrl = params.api_url;
                    documentUploadService.bearerToken = params.bearer;

                    await documentUploadService.updateDocument(
                        job.payload.existing_document_uuid,
                        job.payload.new_document_data,
                        job.payload.entity_type,
                        job.payload.documentable_uuid
                    );

                    console.log(`Successfully updated document ${job.payload.existing_document_uuid}`);
                    // Job completed successfully
                    await this.deleteJob(job);
                    return;
                } else if (job.type === "document-confirm") {
                    // This is handled by uploadDocument, but if we have a standalone confirm job
                    await documentUploadService.confirmUpload(job.payload.session_uuid);

                    await this.deleteJob(job);
                    return;
                }
            } catch (error) {
                console.error(`Error processing ${job.type} job:`, {
                    jobId: job.id,
                    error: error.message,
                    stack: error.stack,
                    payload: job.payload,
                });

                // Don't retry authentication errors - mark as permanently failed
                if (error.message && error.message.includes("401")) {
                    console.error("Authentication error - marking job as permanently failed");
                    await db.permanently_failed_jobs.put(job);
                    await this.deleteJob(job);
                    return;
                }

                throw error;
            }
        } else if (["upload", "upload-document"].includes(job.type)) {
            try {
                const formData = convert.toFormData(job.payload);

                if (job.type === "upload") {
                    formData.append("image", job.image);
                    formData.append("image_uuid", job.image_uuid);
                    formData.append("orientation", job.orientation);
                } else if (job.type === "upload-document") {
                    formData.append("file", job.file);
                }

                options.body = formData;
            } catch (formDataError) {
                console.error(`Error creating FormData for ${job.type} job:`, {
                    jobId: job.id,
                    error: formDataError.message,
                    type: job.type,
                });
                // For image upload errors, we typically want to fail the job rather than retry
                throw new Error(`FormData error: ${formDataError.message}`);
            }
        }

        // Add timeout for the fetch operation
        const controller = new AbortController();
        options.signal = controller.signal;
        const timeoutId = setTimeout(() => controller.abort(), 30000); // 30 second timeout for uploads

        try {
            // Execute the call
            const response = await fetch(`${params.api_url}${job.url}`, options);
            clearTimeout(timeoutId);

            if (!response.ok) {
                // Try to get error details from response
                let errorDetails = "";
                try {
                    const errorBody = await response.text();
                    if (errorBody) {
                        errorDetails = ` - ${errorBody.substring(0, 200)}`; // Limit error message length
                    }
                } catch (e) {
                    // Ignore errors reading response body
                }

                const error = new Error(`Server error: ${response.status}${errorDetails}`);
                error.response = response;
                error.status = response.status;

                // Log detailed error information
                console.error(`Job ${job.id} failed:`, {
                    type: job.type,
                    url: job.url,
                    status: response.status,
                    error: errorDetails,
                    module: job.module,
                });

                // Handle rate limiting (429)
                if (response.status === 429) {
                    // Calculate exponential backoff
                    if (this.currentBackoffDelay === 0) {
                        this.currentBackoffDelay = this.rateLimitBackoff.initialDelay;
                    } else {
                        this.currentBackoffDelay = Math.min(
                            this.currentBackoffDelay * this.rateLimitBackoff.multiplier,
                            this.rateLimitBackoff.maxDelay
                        );
                    }

                    // Set pause until timestamp
                    this.rateLimitPauseUntil = Date.now() + this.currentBackoffDelay;

                    console.warn(`Rate limited (429). Pausing queue for ${this.currentBackoffDelay}ms`);
                }

                throw error;
            }

            // Reset backoff on successful request
            if (this.currentBackoffDelay > 0) {
                this.currentBackoffDelay = 0;
            }

            // Only if it's not a delete or upload action, store the response in Dexie
            // Skip storing response for bulk operations as data is already stored locally
            if (!["delete", "upload", "upload-document"].includes(job.type) && !job.url.includes("/bulk-")) {
                try {
                    const responseData = await response.json();

                    if (Array.isArray(responseData)) {
                        await db[job.module].bulkPut(responseData);
                    } else if (responseData && Object.keys(responseData).length > 0) {
                        await db[job.module].put(responseData);
                    }
                } catch (jsonError) {
                    console.warn(`Error parsing JSON response for job ${job.id}:`, {
                        error: jsonError.message,
                        type: job.type,
                        url: job.url,
                    });
                    // Continue with job deletion since the server accepted it
                }
            }

            // Delete job from jobs store
            await this.deleteJob(job);
        } catch (error) {
            clearTimeout(timeoutId);

            if (error.name === "AbortError") {
                throw new Error(`Fetch timeout for job ${job.id}`);
            }

            // Handle network errors differently from server errors
            if (error.name === "TypeError" && error.message.includes("fetch")) {
                // Network error - likely offline or connection issue
                console.warn(`Network error for job ${job.id}, will retry when connection is restored`);
                error.isNetworkError = true;
            }

            throw error;
        }
    }

    /**
     * Process failed jobs by retrying or moving to permanently failed
     */
    async runFailedJobs(params, cancelToken = null) {
        try {
            const failed_jobs = await db.failed_jobs.toArray();

            for (const failed_job of failed_jobs) {
                // These pre-queue phases can involve a network call per job, so a
                // stopped task must terminate here too — a backlog of failed jobs
                // would otherwise keep the "stopped" task busy for a long time.
                if (cancelToken?.cancelled) {
                    console.log("[runFailedJobs] Task cancelled, stopping");
                    return;
                }

                // Skip if job is marked as permanent failure
                if (failed_job.permanent) {
                    console.log(`Job ${failed_job.id} is marked as permanent failure, moving to permanently failed`);
                    await db.permanently_failed_jobs.put(failed_job);
                    await db.failed_jobs.delete(failed_job.id);
                    continue;
                }

                // Check if this is an orphaned image job (image doesn't exist)
                if (failed_job.type === "image-s3-upload" && failed_job.payload?.image_uuid) {
                    const image = await db.images.get(failed_job.payload.image_uuid);
                    if (!image) {
                        console.log(
                            `Image ${failed_job.payload.image_uuid} not found, permanently failing job ${failed_job.id}`
                        );
                        const permJob = {
                            ...failed_job,
                            permanent: true,
                            error_message: "Image not found in database",
                        };
                        await db.permanently_failed_jobs.put(permJob);
                        await db.failed_jobs.delete(failed_job.id);
                        continue;
                    }
                }

                // Check if job has been failed for too long
                const failedAt = failed_job.failed_at ? new Date(failed_job.failed_at) : new Date();
                const hoursInFailedState = (Date.now() - failedAt.getTime()) / (1000 * 60 * 60);

                // If job has been in failed state for longer than maxFailedJobAge, move to permanently failed
                if (hoursInFailedState > this.maxFailedJobAge) {
                    console.warn(
                        `Job ${failed_job.id} has been failed for ${hoursInFailedState.toFixed(1)} hours (max: ${
                            this.maxFailedJobAge
                        }), moving to permanently failed`
                    );
                    await db.permanently_failed_jobs.put(failed_job);
                    await db.failed_jobs.delete(failed_job.id);
                    await this.postPermanentFailedJob(failed_job, params);
                    continue;
                }

                // Only retry POST, PATCH, DELETE jobs and S3 image/document uploads.
                // The S3 update/document types are included so transient failures
                // (e.g. 500s, which now route through failed_jobs instead of failing
                // permanently on first occurrence) actually get their retries;
                // without them a failed job of these types would be moved straight
                // to permanently_failed on the next queue run.
                const jobType = failed_job.type.toLowerCase();
                const canRetry = [
                    "post",
                    "patch",
                    "patcharray",
                    "delete",
                    "image-s3-upload",
                    "image-s3-update",
                    "image-confirm",
                    "document-s3-upload",
                    "document-s3-update",
                    "document-confirm",
                ].includes(jobType);

                // Special handling for image upload jobs - limit to 3 retries
                const maxAttempts = jobType.includes("image") ? 3 : this.maxAttempts;

                // Make sure attempt is a number
                const currentAttempt = parseInt(failed_job.attempt || 0);

                if (canRetry && currentAttempt < maxAttempts) {
                    // Skip retry if it's a CORS error
                    if (failed_job.cors_error || failed_job.error_message?.includes("CORS")) {
                        console.log(`Job ${failed_job.id} has CORS error, not retrying`);
                        await db.permanently_failed_jobs.put(failed_job);
                        await db.failed_jobs.delete(failed_job.id);
                        await this.postPermanentFailedJob(failed_job, params);
                    } else {
                        // Retry the job - preserve attempt count
                        console.log(`Retrying job ${failed_job.id}, attempt ${currentAttempt + 1}/${maxAttempts}`);
                        const retryJob = {
                            ...failed_job,
                            attempt: currentAttempt, // Keep the current attempt count
                        };
                        delete retryJob.error_message; // Remove error message for retry
                        delete retryJob.failed_at; // Remove failed timestamp

                        await db.jobs.put(retryJob);
                        await db.failed_jobs.delete(failed_job.id);
                    }
                } else {
                    // Move to permanently failed
                    await db.permanently_failed_jobs.put(failed_job);
                    await db.failed_jobs.delete(failed_job.id);
                    // Send the permanently failed job to the server
                    await this.postPermanentFailedJob(failed_job, params);
                }
            }
        } catch (error) {
            console.error("Error in runFailedJobs:", error);
        }
    }

    /**
     * Process permanently failed jobs by sending to server
     */
    async runPermanentlyFailedJobs(params, cancelToken = null) {
        try {
            const perm_failed_jobs = await db.permanently_failed_jobs.toArray();

            for (const failed_job of perm_failed_jobs) {
                // One network call per job — terminate promptly when the task
                // was stopped; remaining jobs are posted on the next run.
                if (cancelToken?.cancelled) {
                    console.log("[runPermanentlyFailedJobs] Task cancelled, stopping");
                    return;
                }

                await this.postPermanentFailedJob(failed_job, params);
            }
        } catch (error) {
            console.error("Error in runPermanentlyFailedJobs:", error);
        }
    }

    /**
     * Move a job to the failed_jobs store
     */
    async toFailedJobs(job, error) {
        try {
            // For rate limiting errors, put the job back in queue without incrementing attempts
            if (error?.status === 429 || error?.response?.status === 429) {
                console.log(`Job ${job.id} rate limited, will retry after cooldown`);
                // Job stays in the queue, will be retried after the rate limit pause
                return;
            }

            // Check for CORS errors specifically
            if (
                error?.isCorsError ||
                error?.message?.includes("CORS_ERROR") ||
                (error?.message?.includes("CORS") && job?.type === "image-s3-upload")
            ) {
                console.error(`Job ${job.id} failed due to CORS configuration error - not retrying`);

                // Move directly to permanently failed - CORS won't fix itself with retries
                const permanentlyFailedJob = {
                    ...job,
                    error_message:
                        "S3 bucket CORS configuration error - POST method not allowed. Contact administrator.",
                    attempt: (job.attempt || 0) + 1,
                    failed_at: new Date().toISOString(),
                    cors_error: true,
                };

                await db.permanently_failed_jobs.put(permanentlyFailedJob);
                await this.deleteJob(job);

                // Update image status if this is an image job
                if (job.payload?.image_uuid) {
                    await db.images.update(job.payload.image_uuid, {
                        upload_status: "cors_error",
                        upload_error: "S3 bucket needs CORS configuration for POST method",
                    });
                }

                return;
            }

            // Check for S3 Policy errors (wrong Content-Type, etc)
            if (
                error?.isPolicyError ||
                error?.message?.includes("Policy Error") ||
                error?.message?.includes("Policy Condition failed") ||
                error?.message?.includes("Invalid according to Policy")
            ) {
                console.error(`Job ${job.id} failed due to S3 Policy error - not retrying`);

                // Move directly to permanently failed - Policy errors need backend/format fixes
                const permanentlyFailedJob = {
                    ...job,
                    error_message: error.message.substring(0, 200),
                    attempt: (job.attempt || 0) + 1,
                    failed_at: new Date().toISOString(),
                    policy_error: true,
                    permanent: true,
                };

                await db.permanently_failed_jobs.put(permanentlyFailedJob);
                await this.deleteJob(job);

                // Update image status if this is an image job
                if (job.payload?.image_uuid) {
                    await db.images.update(job.payload.image_uuid, {
                        upload_status: "policy_error",
                        upload_error: "S3 Policy error - check image format",
                    });
                }

                return;
            }

            // 500s are usually TRANSIENT (a deploy, a database hiccup, a timeout on
            // the backend). They used to be moved straight to permanently_failed_jobs
            // on the first occurrence, which meant a single unlucky request silently
            // dropped the data forever — e.g. a measurement POST during a backend
            // deploy never synced again. Instead, annotate the job and let it take
            // the normal failed_jobs path below: it gets retried up to maxAttempts
            // (and at most maxFailedJobAge hours) before permanently failing, at
            // which point it still lands in the audit trail with the flag set.
            let serverError = false;
            if (error?.message?.includes("500") || error?.status === 500 || error?.response?.status === 500) {
                serverError = true;

                // Categorized message shown on the image/document row while retrying.
                // "Entity not found" deliberately stays retryable (unlike CORS/policy):
                // it can be a transient ordering issue — the parent record's POST may
                // still be queued or in failed_jobs and reach the server before the
                // retry does. If the entity is truly gone, the job still permanently
                // fails after maxAttempts with the flag preserved.
                let retryError = "Server error - will retry automatically";
                if (error?.message?.includes("Entity not found")) {
                    retryError = "Entity not found - parent entity may have been deleted";
                } else if (error?.message?.includes("Failed to prepare upload")) {
                    retryError = "Server failed to prepare upload";
                }

                console.warn(`Job ${job.id} failed with server error (500), scheduling retry via failed_jobs`);

                // Update image status if this is an image job. 'failed' is a retryable
                // status: it stays protected from deletion and is picked up again by
                // the failed_jobs retry (or rescueOrphanedImages). image-s3-update
                // jobs carry their target as existing_image_uuid instead of image_uuid.
                const imageUuid = job.payload?.image_uuid || job.payload?.existing_image_uuid;
                if (imageUuid) {
                    try {
                        await db.images.update(imageUuid, {
                            upload_status: "failed",
                            upload_error: retryError,
                        });
                    } catch (e) {
                        console.log(`Could not update image ${imageUuid}`);
                    }
                }

                // Same for documents: mirror the image behavior so the UI reflects
                // the retryable failure instead of silently retrying in the
                // background. 'failed' is in UNSYNCED_DOCUMENT_STATUSES, so the row
                // stays protected from deletion while retries run. Note that
                // document-confirm payloads only carry a session_uuid, so no row can
                // be flagged for those — acceptable, the job itself still retries.
                const documentUuid = job.payload?.document_uuid || job.payload?.existing_document_uuid;
                if (documentUuid) {
                    try {
                        await db.documents.update(documentUuid, {
                            upload_status: "failed",
                            upload_error: retryError,
                        });
                    } catch (e) {
                        console.log(`Could not update document ${documentUuid}`);
                    }
                }
            }

            // Initialize attempt if not present
            if (job.attempt === undefined || job.attempt === null) {
                job.attempt = 0;
            }

            const attemptCount = job.attempt + 1;

            // Check max attempts for image jobs - limit to 3
            if (job.type === "image-s3-upload" || job.type === "image-confirm") {
                if (attemptCount >= 3) {
                    console.error(`Image upload job ${job.id} exceeded max attempts (3), moving to permanently failed`);

                    // First delete from all possible locations to prevent duplication
                    await this.deleteJob(job);
                    if (job.id) {
                        await db.failed_jobs.where("id").equals(job.id).delete();
                    }

                    const permanentlyFailedJob = {
                        ...job,
                        id: job.id || Date.now() + Math.random(), // Ensure unique ID
                        error_message: `Failed after ${attemptCount} attempts: ${(error.message || "").substring(0, 200)}`,
                        attempt: attemptCount,
                        failed_at: new Date().toISOString(),
                        permanent: true, // Mark as permanent to prevent re-processing
                        // Set explicitly so the audit label reflects the FINAL failure:
                        // an image job whose last attempt failed with a 500 exits
                        // through this block, and a leftover flag from an earlier 500
                        // must not survive a non-500 final failure.
                        server_error: serverError,
                    };

                    await db.permanently_failed_jobs.put(permanentlyFailedJob);

                    // Update image status if we can find it
                    if (job.payload?.image_uuid) {
                        try {
                            const image = await db.images.get(job.payload.image_uuid);
                            if (image) {
                                await db.images.update(job.payload.image_uuid, {
                                    upload_status: "failed",
                                    upload_error: `Upload failed after ${attemptCount} attempts - Image may not exist`,
                                });
                            }
                        } catch (e) {
                            console.log(`Image ${job.payload.image_uuid} not found in database`);
                        }
                    }
                    return;
                }
            }

            const cloned_job = {
                ...job,
                // Cap the stored message like the CORS/policy branches do — backend
                // error bodies can be arbitrarily long.
                error_message: (error.message || "").substring(0, 200),
                attempt: attemptCount,
                failed_at: new Date().toISOString(),
                // Always set explicitly (not conditionally spread): the job object can
                // carry server_error=true from an earlier 500 retry cycle, and a later
                // NON-500 failure must clear it — the flag drives the logout requeue
                // decision and the audit-trail label, so it has to reflect the most
                // recent failure, not a 500 from several attempts ago.
                server_error: serverError,
            };

            // Handle 404 errors more intelligently
            if (error?.response?.status === 404) {
                // For image uploads, check if parent observation/measurement exists locally
                if (job.type === "upload" && job.url && job.url.includes("/images")) {
                    // Extract parent UUID from URL (e.g., /observations/uuid/images)
                    const urlParts = job.url.split("/");
                    let parentIndex = urlParts.indexOf("observations");
                    if (parentIndex === -1) {
                        parentIndex = urlParts.indexOf("measurements");
                    }

                    if (parentIndex !== -1 && urlParts[parentIndex + 1]) {
                        const parentUuid = urlParts[parentIndex + 1];
                        const parentModule = urlParts[parentIndex];

                        // Check if parent exists in queue or locally. payload.uuid is not an
                        // indexed keypath, so where("payload.uuid") would throw a SchemaError
                        // (silently caught below, leaving the job stuck in the queue forever)
                        // — use filter() instead.
                        const pendingParentJob = await db.jobs
                            .filter((j) => j.payload?.uuid === parentUuid)
                            .first();

                        const failedParentJob = await db.failed_jobs
                            .filter((j) => j.payload?.uuid === parentUuid)
                            .first();

                        if (pendingParentJob || failedParentJob) {
                            // Parent is still pending or failed, retry this job later
                            console.log(
                                `Image upload job ${job.id} failed with 404, but parent ${parentUuid} is still pending/failed. Will retry.`
                            );
                            cloned_job.error_message = `Parent resource ${parentUuid} not yet synced`;

                            // Check if already in failed_jobs to prevent duplicates. Jobs created
                            // before uuids were added at every creation site have none — querying
                            // equals(undefined) would throw and leave the job stuck in the queue.
                            const existingFailed = job.uuid
                                ? await db.failed_jobs.where("uuid").equals(job.uuid).first()
                                : null;
                            if (!existingFailed) {
                                await db.failed_jobs.put(cloned_job);
                            }
                            await this.deleteJob(job);
                            return;
                        }
                    }
                }

                // Otherwise: log to permanently_failed_jobs so we have an audit trail
                // (previous behaviour: silent deleteJob — data-loss risk when POST 404s)
                console.log(`Job ${job.id} failed with 404, moving to permanently_failed_jobs`);
                const permanent404Job = {
                    ...job,
                    error_message: `404 — resource not found at ${job.url}`,
                    failed_at: new Date().toISOString(),
                    permanent: true,
                };
                await db.permanently_failed_jobs.put(permanent404Job);
                await this.deleteJob(job);
                return;
            }

            // Check if already in failed_jobs to prevent duplicates. Jobs created before
            // uuids were added at every creation site have none — querying
            // equals(undefined) throws, which used to leave the job stuck in the jobs
            // table forever: never moved to failed_jobs, attempt never persisted, so it
            // retried endlessly on every queue run.
            const existingFailed = job.uuid
                ? await db.failed_jobs.where("uuid").equals(job.uuid).first()
                : null;
            if (!existingFailed) {
                await db.failed_jobs.put(cloned_job);
            }
            await this.deleteJob(job);
        } catch (err) {
            console.error(`Error in toFailedJobs for job ${job.id}:`, err);
        }
    }

    /**
     * Delete a job from the jobs store
     */
    async deleteJob(job) {
        try {
            await db.jobs.delete(job.id);
        } catch (err) {
            console.error(`Error deleting job ${job.id}:`, err);
        }
    }

    /**
     * Post permanently failed job to the server
     */
    async postPermanentFailedJob(job, params) {
        try {
            if (!params.online && !params.ignoreOffline) {
                return;
            }

            // Create a sanitized copy of the job to send to backend
            const jobToSend = { ...job };

            // For S3 update jobs, remove the heavy base64 data from payload
            if (job.type === "image-s3-update" && job.payload) {
                jobToSend.payload = { ...job.payload };

                // Remove the base64 image data (can be several MB)
                if (jobToSend.payload.new_image_data) {
                    delete jobToSend.payload.new_image_data;
                }

                // Try to include S3 key if the image exists
                if (job.payload.existing_image_uuid) {
                    const image = await db.images.get(job.payload.existing_image_uuid);
                    if (image && image.s3_key) {
                        jobToSend.s3_key = image.s3_key;
                    }
                }
            }

            // For S3 upload jobs, include S3 key if the upload partially succeeded
            if (job.type === "image-s3-upload" && job.payload && job.payload.image_uuid) {
                const image = await db.images.get(job.payload.image_uuid);
                if (image && image.s3_key) {
                    jobToSend.s3_key = image.s3_key;
                }
            }

            // For document S3 update jobs, remove the heavy base64 data from payload
            if (job.type === "document-s3-update" && job.payload) {
                jobToSend.payload = { ...job.payload };

                // Remove the base64 document data (can be several MB)
                if (jobToSend.payload.new_document_data) {
                    delete jobToSend.payload.new_document_data;
                }

                // Try to include S3 key if the document exists
                if (job.payload.existing_document_uuid) {
                    const document = await db.documents.get(job.payload.existing_document_uuid);
                    if (document && document.s3_key) {
                        jobToSend.s3_key = document.s3_key;
                    }
                }
            }

            // For document S3 upload jobs, include S3 key if the upload partially succeeded
            if (job.type === "document-s3-upload" && job.payload && job.payload.document_uuid) {
                const document = await db.documents.get(job.payload.document_uuid);
                if (document && document.s3_key) {
                    jobToSend.s3_key = document.s3_key;
                }
            }

            // Set a timeout for the fetch operation
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), 30000); // 30 second timeout

            const response = await fetch(`${params.api_url}/failed-jobs`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Accept: "application/vnd.api+json",
                    ...(params.bearer && { Authorization: `Bearer ${params.bearer}` }),
                },
                body: JSON.stringify({ data: jobToSend }),
                signal: controller.signal,
            });

            clearTimeout(timeoutId);

            if (!response.ok) {
                const error = new Error(`Server error: ${response.status}`);
                error.response = response;
                throw error;
            }

            // Delete the permanently failed job if the server received it correctly
            await db.permanently_failed_jobs.delete(job.id);
        } catch (error) {
            console.error("Permanently failed job error:", error);
            // Don't rethrow - we want to continue processing other jobs
        }
    }

    /**
     * Clean up floating records (orphaned components) in the database
     *
     * SCOPING DECISION: Current Location Only
     *
     * This function is scoped to the current inspection's location (not all locations).
     * This is a deliberate trade-off for memory and performance:
     *
     * Why scoped to current location:
     * - Memory: Loading ALL components from ALL locations could be 100MB+ on devices with many inspections
     * - Performance: Checking only relevant components is much faster (2-5MB vs 100MB+)
     * - Frequency: Called during every sync - full cleanup would cause lag/crashes on every sync
     * - Context: When syncing, we're typically working with the current location's data
     *
     * Trade-off acknowledged:
     * - Incomplete cleanup: Floating records in other locations won't be cleaned until user visits them
     * - Potential bloat: If user has inspections on many locations they never revisit, orphaned records may accumulate
     * - Not a critical issue: Floating records don't affect app functionality, only database size
     *
     * Cross-inspection cleanup:
     * - Components are validated across ALL inspections on the SAME location (line 1295)
     * - This handles the common case: multiple inspections on the same location
     * - Only orphaned records on DIFFERENT locations are left uncleaned
     *
     * Future enhancement considerations:
     * - Add periodic full cleanup (e.g., once per week, when app is idle)
     * - Add manual "Clean Database" button in settings for power users
     * - Monitor database size and trigger full cleanup if it exceeds threshold
     * - Run full cleanup in background with lower priority
     *
     * Memory optimization:
     * - Uses cursor-based iteration (.each) instead of .toArray()
     * - Prevents loading all components into memory at once
     * - Before: 100MB+ spike, After: 2-5MB usage
     */
    async deleteFloatingRecords() {
        // Prevent concurrent cleanup runs
        if (this._cleanupInProgress) {
            console.log('[deleteFloatingRecords] Cleanup already in progress, skipping');
            return;
        }

        this._cleanupInProgress = true;

        try {
            // Get current inspection ID from store
            const currentInspectionId = store.getters.getInspectionId;

            if (!currentInspectionId) {
                console.log('[deleteFloatingRecords] No current inspection, skipping cleanup');
                return;
            }

            // Get the current inspection to find its location
            const inspection = await db.inspections.get(currentInspectionId);
            if (!inspection || !inspection.location_id) {
                console.log('[deleteFloatingRecords] No location found for current inspection');
                return;
            }

            // Capture location ID to detect context changes during async operations
            const currentLocationId = inspection.location_id;

            console.log(`[deleteFloatingRecords] Cleaning up floating records for location ID ${currentLocationId}`);

            /**
             * SCOPING: Filter by location_id (not all locations)
             *
             * This query only fetches location_items for the CURRENT location.
             * Components from other locations are not checked during this cleanup.
             *
             * Why: Prevents 100MB+ memory spike from loading all components
             * Trade-off: Orphaned records on other locations remain until user visits them
             * Coverage: ALL inspections on THIS location are included (cross-inspection cleanup)
             */
            const locationItemUuids = new Set();
            await db.location_items
                .where('location_id')
                .equals(currentLocationId)
                .each(item => {
                    locationItemUuids.add(item.uuid);
                });

            console.log(`[deleteFloatingRecords] Found ${locationItemUuids.size} location items for this location`);

            // MEMORY OPTIMIZATION: Use cursor-based iteration instead of toArray()
            // This prevents loading all components into memory at once (could be 100MB+)
            // PERFORMANCE OPTIMIZATION: Single-pass algorithm instead of two separate iterations

            const validUuids = new Set();
            const componentsToCheck = [];

            // Single pass: collect all valid UUIDs and components that need parent checking
            await db.location_item_components
                .where('location_item_uuid')
                .anyOf([...locationItemUuids])
                .each(component => {
                    // Every component is valid (exists in this location)
                    validUuids.add(component.uuid);

                    // Track components that have parents to check later
                    if (component.parent_uuid) {
                        componentsToCheck.push({
                            uuid: component.uuid,
                            parent_uuid: component.parent_uuid
                        });
                    }
                });

            console.log(`[deleteFloatingRecords] Found ${validUuids.size} valid components for this location`);

            // Step 2: Check which components have invalid parents (orphaned)
            // This is now just in-memory Set lookups, very fast
            const orphanedUuids = [];
            for (const component of componentsToCheck) {
                if (!validUuids.has(component.parent_uuid)) {
                    orphanedUuids.push(component.uuid);
                }
            }

            // Step 3: Delete orphaned components in batches to avoid memory spikes
            if (orphanedUuids.length > 0) {
                // RACE CONDITION GUARD: Verify inspection hasn't changed during async operations
                // If user switched inspections while cleanup was running, abort to prevent confusion
                if (currentInspectionId !== store.getters.getInspectionId) {
                    console.log('[deleteFloatingRecords] Inspection changed during cleanup, aborting deletion for safety');
                    return;
                }

                console.log(`[deleteFloatingRecords] Deleting ${orphanedUuids.length} orphaned components`);

                const BATCH_SIZE = 100;
                for (let i = 0; i < orphanedUuids.length; i += BATCH_SIZE) {
                    // RACE CONDITION GUARD: Check again before each batch for extra safety
                    // This minimizes the race window to near-zero
                    if (currentInspectionId !== store.getters.getInspectionId) {
                        console.log('[deleteFloatingRecords] Inspection changed during batch deletion, aborting for safety');
                        return;
                    }

                    const batch = orphanedUuids.slice(i, i + BATCH_SIZE);
                    await db.location_item_components.bulkDelete(batch);
                }

                console.log(`[deleteFloatingRecords] Cleanup complete - deleted ${orphanedUuids.length} orphaned records`);
            } else {
                console.log('[deleteFloatingRecords] No orphaned records found');
            }

        } catch (error) {
            // Don't fail the entire sync if cleanup fails
            console.error('[deleteFloatingRecords] Error during cleanup:', error);
        } finally {
            this._cleanupInProgress = false;
        }
    }

    /**
     * Merge attribute_values from optimized API with base component schema
     * @param {Object} locationItemComponent - Component with attribute_values
     * @returns {Object} Component with full attributes.fields
     */
    async mergeComponentAttributes(locationItemComponent) {
        // If no attribute_values, initialize empty
        if (!locationItemComponent.attribute_values) {
            locationItemComponent.attributes = { fields: [] };
            return locationItemComponent;
        }

        // Load base component schema
        const baseComponent = await db.base_components.where({ uuid: locationItemComponent.component_uuid }).first();

        if (!baseComponent) {
            console.warn(`Base component not found in database for UUID: ${locationItemComponent.component_uuid}`);
            locationItemComponent.attributes = { fields: [] };
            return locationItemComponent;
        }

        if (!baseComponent.attributes || !baseComponent.attributes.fields) {
            // Component has no attributes schema defined - this is OK for simple components
            locationItemComponent.attributes = { fields: [] };
            return locationItemComponent;
        }

        // Create lookup map for saved values
        const valuesMap = {};
        locationItemComponent.attribute_values.forEach((item) => {
            valuesMap[item.uuid] = item.value;
        });

        // Merge: Start with schema, apply saved values
        const mergedFields = baseComponent.attributes.fields.map((field) => {
            return {
                ...field, // Full schema (label, options, type, rules, placeholder, etc.)
                value: valuesMap[field.uuid] !== undefined ? valuesMap[field.uuid] : field.value || null,
            };
        });

        // Set merged attributes and clean up temporary field
        locationItemComponent.attributes = {
            fields: mergedFields,
        };

        delete locationItemComponent.attribute_values; // Remove temporary field

        return locationItemComponent;
    }

    /**
     * Synchronize an inspection
     */
    async syncInspection(params, updateProgress, cancelToken = null) {
        const { api_url, bearer, inspection_id, online } = params;

        // Check network status - don't proceed if offline
        if (!online && !params.ignoreOffline) {
            throw new Error("Cannot sync inspection while offline");
        }

        if (!inspection_id || typeof inspection_id !== "number") {
            console.error("Invalid inspection_id received:", inspection_id, "Type:", typeof inspection_id);
            throw new Error(
                `Invalid inspection_id received: ${JSON.stringify(inspection_id)} (type: ${typeof inspection_id})`
            );
        }

        if (!api_url || !bearer) {
            throw new Error("Missing API configuration for inspection sync");
        }

        updateProgress(0, "Starting inspection sync");

        // Fetch inspection data
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 30000); // 30 second timeout

        try {
            const response = await fetch(`${api_url}/inspections/${inspection_id}/optimized`, {
                headers: {
                    Authorization: `Bearer ${bearer}`,
                    Accept: "application/vnd.api+json",
                    ContentType: "application/vnd.api+json",
                },
                signal: controller.signal,
            });

            clearTimeout(timeoutId);

            if (!response.ok) {
                throw new Error(`Failed to fetch inspection data: ${response.status}`);
            }

            const inspection = await response.json();

            // The fetch can take a while — don't start writing to the local
            // database if the task was stopped in the meantime.
            if (cancelToken?.cancelled) {
                console.log("[syncInspection] Task cancelled after fetch, skipping processing");
                return;
            }

            updateProgress(10, `Syncing inspection: ${inspection.name}`);

            // Process inspection data with chunking for better UI responsiveness
            await this.processInspectionData(inspection, (progress, detail) => {
                // Scale progress from 10-100%
                updateProgress(10 + progress * 0.9, detail);
            }, cancelToken);

            updateProgress(100, "Inspection sync complete");
        } catch (error) {
            clearTimeout(timeoutId);

            if (error.name === "AbortError") {
                throw new Error("Inspection data fetch timed out");
            }

            throw error;
        }
    }

    /**
     * Process inspection data
     */
    async processInspectionData(inspection, progressCallback, cancelToken = null) {
        let nonImageTasks = [];
        let imageTasks = [];

        // Build task lists for different parts of the inspection data
        // Samples
        if (inspection.samples) {
            nonImageTasks.push(async () => {
                await this.add("samples", inspection.samples, { inspection_id: inspection.id });
            });
        }

        // SCIOS codes
        if (inspection.scios_codes) {
            nonImageTasks.push(async () => {
                await this.add("scios_codes", inspection.scios_codes, { inspection_id: inspection.id });
            });
        }

        // Location systems
        if (inspection.location_systems) {
            nonImageTasks.push(async () => {
                await Promise.all(
                    inspection.location_systems.map((ls) =>
                        this.add("location_systems", ls, { location_systemable_uuid: inspection.location.uuid })
                    )
                );
            });
        }

        // Project
        if (inspection.project) {
            nonImageTasks.push(async () => {
                inspection.project.inspection_id = inspection.id;
                await this.add("projects", [inspection.project], { inspection_id: inspection.id });
            });
        }

        // Customer
        if (inspection.customer) {
            nonImageTasks.push(async () => {
                inspection.customer.inspection_id = inspection.id;
                await this.add("customers", [inspection.customer], { inspection_id: inspection.id });
            });
        }

        // Customer contact
        if (inspection.customer_contact && inspection.customer?.uuid) {
            nonImageTasks.push(async () => {
                const customer = await db.customers.get({ uuid: inspection.customer.uuid });
                if (customer) {
                    customer.customer_contact = inspection.customer_contact;
                    await db.customers.put(customer);
                }
            });
        }

        // Inspector users
        if (inspection.inspector_users) {
            nonImageTasks.push(async () => {
                inspection.inspector_users.forEach((user) => {
                    user.inspection_id = inspection.id;
                    user.id = `${inspection.id}-${user.id}`;
                });
                await this.add(
                    "inspector_users",
                    inspection.inspector_users,
                    { inspection_id: inspection.id },
                    "inspection_id"
                );
            });
        }

        // Location (without location_items)
        if (inspection.location) {
            nonImageTasks.push(async () => {
                const location = { ...inspection.location, inspection_id: inspection.id };
                delete location.location_items;
                await this.add("locations", [location], { inspection_id: inspection.id });

                if (inspection.location.location_systems) {
                    await this.add("location_systems", inspection.location.location_systems, {
                        location_systemable_uuid: inspection.location.uuid,
                    });
                }
            });

            // Add location images to imageTasks
            if (inspection.location.images) {
                imageTasks.push(async () => {
                    return this.add("images", inspection.location.images, { imageable_uuid: inspection.location.uuid });
                });
            }
        }

        // Documents
        if (inspection.documents) {
            nonImageTasks.push(async () => {
                inspection.documents.forEach((doc) => {
                    doc.documentable_uuid = inspection.uuid;
                });
                await this.add("documents", inspection.documents, { documentable_uuid: inspection.uuid });
            });
        }

        // Location items and components
        if (inspection.location && inspection.location.location_items) {
            const locationItems = inspection.location.location_items;

            // First process components for each location item
            for (const locationItem of locationItems) {
                nonImageTasks.push(async () => {
                    if (locationItem.location_item_components) {
                        // Merge attribute_values with base component schemas
                        const mergedComponents = [];
                        for (const component of locationItem.location_item_components) {
                            const merged = await this.mergeComponentAttributes(component);
                            mergedComponents.push(merged);
                        }

                        // Store merged components
                        await this.add("location_item_components", mergedComponents, {
                            location_item_uuid: locationItem.uuid,
                        });

                        // Process nested data (images, observations, measurements, etc.)
                        for (const component of mergedComponents) {
                            // Component images
                            if (component.images) {
                                imageTasks.push(async () => {
                                    return this.add("images", component.images, { imageable_uuid: component.uuid });
                                });
                            }

                            // Observations
                            if (component.observations) {
                                await this.add("observations", component.observations, {
                                    location_item_component_uuid: component.uuid,
                                });

                                for (const observation of component.observations) {
                                    if (observation.images) {
                                        imageTasks.push(async () => {
                                            return this.add("images", observation.images, {
                                                imageable_uuid: observation.uuid,
                                            });
                                        });
                                    }
                                }
                            }

                            // Measurements
                            if (component.measurements) {
                                await this.add("measurements", component.measurements, {
                                    location_item_component_uuid: component.uuid,
                                });

                                for (const measurement of component.measurements) {
                                    if (measurement.images) {
                                        imageTasks.push(async () => {
                                            return this.add("images", measurement.images, {
                                                imageable_uuid: measurement.uuid,
                                            });
                                        });
                                    }
                                }
                            }

                            // Documents
                            if (component.documents) {
                                await this.add("documents", component.documents, { documentable_uuid: component.uuid });
                            }

                            // Answers
                            if (component.answers) {
                                await this.add("answers_location_item_components", component.answers, {
                                    location_item_component_uuid: component.uuid,
                                });
                            }

                            // Location systems
                            if (component.location_systems) {
                                await this.add("location_systems", component.location_systems, {
                                    location_systemable_uuid: component.uuid,
                                });
                            }
                        }
                    }
                });
            }

            // Then add all location_items in one bulk call
            nonImageTasks.push(async () => {
                await this.add("location_items", locationItems, { location_id: inspection.location.id });
            });
        }

        // Forms
        if (inspection.forms) {
            nonImageTasks.push(async () => {
                // Upsert the global form catalog (definition + questions). module_parser
                // strips the per-inspection fields (pivot/answers/inspection_id); the
                // inspection link is tracked in the forms_inspections junction below.
                const parsedForms = await module_parser("forms", inspection.forms);
                const validForms = parsedForms.filter((form) => form && form.id != null);
                if (validForms.length > 0) await db.forms.bulkPut(validForms);

                // Replace THIS inspection's form links atomically. Scoped to
                // inspection.id, so links to other inspections are left intact — this is
                // what lets a form belong to multiple inspections at once. The
                // transaction prevents a crash between the delete and the bulkPut from
                // leaving the inspection with zero links. Derive links from validForms so
                // a form with a missing id never produces a { form_id: null } orphan; the
                // delete always runs so stale links are cleared even when there are none.
                const inspectionId = parseInt(inspection.id, 10);
                const links = validForms.map((form) => ({ form_id: form.id, inspection_id: inspectionId }));
                await db.transaction("rw", db.forms_inspections, async () => {
                    await db.forms_inspections.where("inspection_id").equals(inspectionId).delete();
                    if (links.length > 0) await db.forms_inspections.bulkPut(links);
                });
            });

            // Form answers
            const answers = [];
            inspection.forms.forEach((form) => {
                if (form.answers) {
                    answers.push(...form.answers);
                }
            });

            if (answers.length > 0) {
                nonImageTasks.push(async () => {
                    await this.add("answers_inspections", answers, { inspection_id: inspection.id });
                });
            }
        }

        // To improve UI responsiveness, divide tasks into chunks and yield between processing

        // Execute non-image tasks (0% to 60%)
        const totalNonImageTasks = nonImageTasks.length;
        const nonImageTaskWeight = totalNonImageTasks > 0 ? 60 / totalNonImageTasks : 60;

        // Process non-image tasks in small batches
        const nonImageChunks = this.chunkArray(nonImageTasks, 5);

        for (let i = 0; i < nonImageChunks.length; i++) {
            // Stop writing when the task was cancelled; the next sync re-pulls
            // everything, so a partially processed inspection self-heals.
            if (cancelToken?.cancelled) {
                console.log(`[processInspectionData] Task cancelled at data chunk ${i + 1}/${nonImageChunks.length}, stopping`);
                return;
            }

            const taskChunk = nonImageChunks[i];

            // Execute all tasks in this chunk
            await Promise.all(taskChunk.map((task) => task()));

            // Report progress
            progressCallback(
                (i + 1) * nonImageTaskWeight * (taskChunk.length / totalNonImageTasks),
                `Processing data chunk ${i + 1}/${nonImageChunks.length}`
            );

            // Yield to UI thread between chunks
            await new Promise((resolve) => setTimeout(resolve, 0));
        }

        // Execute image tasks (60% to 90%)
        const totalImageTasks = imageTasks.length;

        // MEMORY OPTIMIZATION: Dynamic batch size based on platform
        // Android devices often have less memory, especially low-end ones
        let imageBatchSize = 3; // Default for web
        if (this.platform === "android") {
            imageBatchSize = 2; // Smaller batches for Android to prevent OOM crashes
        } else if (this.platform === "ios") {
            imageBatchSize = 3; // iOS handles memory better
        }

        console.log(`[processInspectionData] Processing ${totalImageTasks} images in batches of ${imageBatchSize}`);

        // Process image tasks in small batches
        const imageChunks = this.chunkArray(imageTasks, imageBatchSize);

        for (let i = 0; i < imageChunks.length; i++) {
            // Image downloads dominate sync time — this is where cancellation
            // matters most in practice.
            if (cancelToken?.cancelled) {
                console.log(`[processInspectionData] Task cancelled at image batch ${i + 1}/${imageChunks.length}, stopping`);
                return;
            }

            const taskChunk = imageChunks[i];
            // How many images this batch actually downloaded — drives the GC pause below.
            let downloadedInBatch = 0;

            try {
                // MEMORY OPTIMIZATION: Process all images in batch in parallel
                // Safe because batches are small (2-3 images) AND sequential batch processing
                // prevents memory accumulation. Already-synced images skip quickly (timestamp check).
                // Max memory per batch: Android 2×5MB=10MB, iOS/Web 3×5MB=15MB
                // Each task returns how many images it downloaded (0 when already local).
                const batchResults = await Promise.all(taskChunk.map((task) => task()));
                downloadedInBatch = batchResults.reduce((sum, n) => sum + (n || 0), 0);

                // Report progress after batch completion
                const processedImages = Math.min((i + 1) * imageBatchSize, totalImageTasks);
                const imageProgress = (processedImages / totalImageTasks) * 35; // 0 to 35%
                progressCallback(
                    60 + imageProgress,
                    `Processing images ${processedImages}/${totalImageTasks}`
                );

            } catch (chunkError) {
                /**
                 * ERROR HANDLING: Continue despite failures
                 *
                 * Design principle: We never exit on error because we want to retrieve as much data as possible.
                 *
                 * Why continue processing:
                 * - Stopping would prevent retrieving other valid images/data
                 * - One failed image shouldn't block the entire sync
                 * - User gets maximum benefit even with partial network failures
                 *
                 * Automatic retry mechanism:
                 * - Failed images don't get marked sync_status = 'synced' (see add() method line 2021-2024)
                 * - Next sync will detect missing sync_status and automatically retry download
                 * - Skip logic (line 1953) only applies to successfully synced images with base64 data
                 * - No manual retry needed - it's built into the sync flow
                 *
                 * Trade-off acknowledged:
                 * - User isn't immediately notified of failed images
                 * - Progress reporting continues as if all succeeded (by design - shows overall progress)
                 * - Failed items simply retry on next sync (transparent to user)
                 */
                console.error(`[processInspectionData] Error processing image chunk ${i + 1}:`, chunkError);
                // Continue to next chunk - failed images will be automatically retried on next sync.
                // A mid-flight download may have allocated a blob, so pause to be safe.
                downloadedInBatch = 1;
            }

            // MEMORY OPTIMIZATION: Yield between batches to allow garbage collection — but
            // ONLY when this batch actually downloaded image blobs. When every image was
            // already local there is nothing to collect, so skip the wait. For a fully
            // cached inspection with hundreds of images this removes the bulk of the time
            // (e.g. ~320 batches × 50ms ≈ 16s of pure waiting for 959 already-local images).
            if (downloadedInBatch > 0) {
                await new Promise((resolve) => setTimeout(resolve, 50));
            }
        }

        console.log(`[processInspectionData] Completed processing ${totalImageTasks} images`);

        // Don't finalize (store the inspection record) for a cancelled task — the
        // inspection row acts as the marker that a sync fully completed.
        if (cancelToken?.cancelled) {
            console.log("[processInspectionData] Task cancelled before finalization, stopping");
            return;
        }

        // Finalize: parse and save inspection (95% to 100%)
        progressCallback(95, "Finalizing inspection data");
        const parsed_inspection = await module_parser("inspections", [inspection]);
        await db.inspections.bulkPut(parsed_inspection);

        progressCallback(100, "Inspection sync complete");
    }

    /**
     * Helper: Add or update items in the database
     */
    async add(module, payload, params, identifier_name = "uuid") {
        if (!payload) return 0;

        try {
            // Count how many images this call actually downloaded (fetched a blob for).
            // The caller (image batch loop) uses this to skip the per-batch GC pause when
            // nothing was downloaded — a fully-cached inspection has no blobs to collect.
            let downloadedImages = 0;

            // First check if database is open
            if (!db.isOpen()) {
                console.warn(`Database not open for module ${module}, attempting to open`);
                try {
                    await db.open();
                    console.log(`Database opened for module ${module}`);
                } catch (dbError) {
                    console.error(`Failed to open database for module ${module}:`, dbError);
                    return 0; // Exit if we can't open the database
                }
            }

            // Validate the payload is an array
            if (!Array.isArray(payload)) {
                console.error(`Invalid payload for module ${module}: not an array`);
                return 0;
            }

            // Filter out items with invalid identifiers
            const validPayload = payload.filter((item) => {
                if (!item || typeof item !== "object") {
                    console.warn(`Skipping invalid item in ${module}: not an object`);
                    return false;
                }

                if (item[identifier_name] === undefined || item[identifier_name] === null) {
                    console.warn(`Skipping item with missing identifier ${identifier_name} in module ${module}`);
                    return false;
                }

                return true;
            });

            // Ensure all params are defined
            for (const key in params) {
                if (params[key] === undefined || params[key] === null) {
                    console.warn(
                        `Parameter ${key} is ${params[key]} for module ${module}, skipping database operation`
                    );
                    return 0;
                }
            }

            const localItems = await db[module]
                .where(params)
                .primaryKeys()
                .then((keys) => {
                    if (keys.length === 0) return [];
                    return db[module].where(identifier_name).anyOf(keys).toArray();
                })
                .catch((error) => {
                    console.error(`Error fetching local items for ${module}:`, error);
                    return []; // Return empty array on error
                });

            if (validPayload.length === 0) {
                if (localItems.length > 0) {
                    try {
                        // For images and documents, don't delete items that haven't been uploaded to
                        // the server yet — the server has no record of them, but they still exist
                        // only locally. The same applies to any locally-created record whose POST
                        // hasn't been accepted yet (not_synced flag).
                        let itemsToDelete = localItems.filter((item) => !isRecordUnsynced(item));
                        if (module === "images") {
                            itemsToDelete = itemsToDelete.filter((item) => !isImageUnsynced(item));
                        } else if (module === "documents") {
                            itemsToDelete = itemsToDelete.filter((item) => !isDocumentUnsynced(item));
                        }

                        if (itemsToDelete.length > 0) {
                            await db[module]
                                .where(identifier_name)
                                .anyOf(itemsToDelete.map((item) => item[identifier_name]))
                                .delete();
                        }
                    } catch (deleteError) {
                        console.error(`Error deleting items from ${module}:`, deleteError);
                    }
                }
                return 0;
            }

            const localMap = Object.fromEntries(localItems.map((item) => [item[identifier_name], item]));
            const itemsToUpdateOrCreate = [];
            const identifiersToKeep = new Set();
            const unsyncedFlagsToClear = [];

            for (const remoteItem of validPayload) {
                // Ensure the item has all required properties
                if (!remoteItem.updated_at) {
                    console.warn(`Item in ${module} missing updated_at, using current time`);
                    remoteItem.updated_at = new Date().toISOString();
                }

                const localItem = localMap[remoteItem[identifier_name]];
                identifiersToKeep.add(remoteItem[identifier_name]);

                // The server response contains this record, so the server clearly knows it.
                // Clear a leftover not_synced flag so future pulls can delete the record
                // again once the server drops it. Needed because bulk-POST responses are
                // never stored locally (see processJob), which otherwise leaves the flag
                // set forever and the record permanently protected from deletion.
                if (localItem && isRecordUnsynced(localItem)) {
                    unsyncedFlagsToClear.push(localItem[identifier_name]);
                }

                // Defensive: a server response must never (re)introduce the flag locally.
                // If a backend endpoint ever echoes the client payload verbatim, storing
                // it below would re-arm the permanent deletion protection.
                delete remoteItem.not_synced;

                // Skip if item exists and updated_at is the same
                if (
                    localItem &&
                    new Date(remoteItem.updated_at).getTime() === new Date(localItem.updated_at).getTime()
                ) {
                    continue;
                }

                // Add for debugging: log when there's a timestamp mismatch causing redownload
                // Special handling for images to avoid re-downloading images we just uploaded
                if (module === "images" && localItem) {
                    // Backend's images.url field contains full S3 URL (DB stores path, API returns full URL)
                    // Extract S3 key from URL to compare with local s3_key
                    const extractS3KeyFromUrl = (url) => {
                        if (!url) return null;
                        try {
                            const urlObj = new URL(url);
                            // Remove leading slash and return the path (which is the S3 key)
                            return urlObj.pathname.replace(/^\//, "");
                        } catch (e) {
                            // If URL parsing fails, assume it's already just the S3 key
                            return url;
                        }
                    };

                    const localS3Key = localItem.s3_key || null;
                    const remoteS3Key = extractS3KeyFromUrl(remoteItem.url);

                    // Skip download if we already have this image synced
                    if (localItem.sync_status === "synced" && localS3Key && localItem.base64) {
                        // Case 1: S3 keys match - definitely the same image
                        // Case 2: Remote has no S3 key but we do and are synced - we just uploaded it
                        if (localS3Key === remoteS3Key || (!remoteS3Key && localS3Key)) {
                            console.log(`Skipping download for ${remoteItem.uuid} - already synced locally`);
                            // Still update metadata (in case other fields like title changed)
                            const metadataUpdate = { ...remoteItem };
                            delete metadataUpdate.url; // Don't need to download
                            metadataUpdate.base64 = localItem.base64; // Keep existing image data
                            metadataUpdate.s3_key = localS3Key; // Keep local S3 key
                            metadataUpdate.sync_status = 'synced'; // Maintain synced status
                            metadataUpdate.upload_status = metadataUpdate.upload_status || 'confirmed';
                            itemsToUpdateOrCreate.push(metadataUpdate);
                            continue;
                        }
                    }
                }

                // Add for update if remote is newer or item doesn't exist
                if (!localItem || new Date(remoteItem.updated_at) > new Date(localItem.updated_at)) {
                    if (module === "images") {
                        try {
                            // Log download with reason and S3 key details
                            let reason;
                            if (!localItem) {
                                reason = "new image";
                            } else {
                                // Extract S3 key from URL for comparison
                                const extractS3KeyFromUrl = (url) => {
                                    if (!url) return null;
                                    try {
                                        const urlObj = new URL(url);
                                        return urlObj.pathname.replace(/^\//, "");
                                    } catch (e) {
                                        return url;
                                    }
                                };

                                const localS3Key = localItem.s3_key || null;
                                const remoteS3Key = extractS3KeyFromUrl(remoteItem.url);

                                if (localS3Key !== remoteS3Key) {
                                    reason = "different S3 key";
                                    console.log(`  Local S3 key:  ${localS3Key ? `"${localS3Key}"` : "null"}`);
                                    console.log(`  Remote S3 key: ${remoteS3Key ? `"${remoteS3Key}"` : "null"}`);
                                } else {
                                    reason = "updated timestamp";
                                }
                            }
                            console.log(`Downloading image ${remoteItem.uuid}: ${reason}`);

                            // Fetch and convert image to base64
                            const blob = await this.fetchBlob(remoteItem.url);
                            // Count the blob the moment it is fetched (it is now in memory),
                            // so the batch-level GC pause still fires even when the base64
                            // conversion below throws (network ok, decode fails).
                            if (blob) downloadedImages++;
                            remoteItem.base64 = blob ? await this.blobToBase64(blob) : null;
                            delete remoteItem.blob;

                            // Extract and store S3 key from URL for future comparisons
                            if (remoteItem.url && !remoteItem.s3_key) {
                                try {
                                    const urlObj = new URL(remoteItem.url);
                                    remoteItem.s3_key = urlObj.pathname.replace(/^\//, "");
                                } catch (e) {
                                    // If URL parsing fails, use url as-is (might already be S3 key)
                                    remoteItem.s3_key = remoteItem.url;
                                }
                            }

                            // Mark images from backend as synced (only if download succeeded)
                            if (remoteItem.base64) {
                                remoteItem.sync_status = 'synced';
                                remoteItem.upload_status = remoteItem.upload_status || 'confirmed';
                            }
                        } catch (imageError) {
                            console.error(`Error processing image for ${remoteItem[identifier_name]}:`, imageError);
                            // Still add the item even if image processing fails
                        }
                    }
                    itemsToUpdateOrCreate.push(remoteItem);
                }
            }

            // Delete local items not in remote data
            // For forms and answers, we should be more careful about deletion to avoid
            // accidentally deleting data from other inspections when syncing
            const shouldDeleteMissingItems = module !== "forms" && module !== "answers_inspections";

            const identifiersToDelete = localItems
                .filter((item) => !identifiersToKeep.has(item[identifier_name]))
                // Never delete locally-created records whose POST hasn't been accepted by the
                // server yet (not_synced flag) — the server response can't contain them, and
                // deleting here would wipe the only copy while its job awaits (re)try.
                .filter((item) => !isRecordUnsynced(item))
                // For images, never delete items that haven't been confirmed on the server yet —
                // pending/failed images only live locally and the server has no record of them.
                .filter((item) => module !== "images" || !isImageUnsynced(item))
                // Same protection for documents: a just-created document that has been uploaded to
                // S3 but whose confirm has not yet come back in a sync response only lives locally,
                // so a sync must not wipe it before the server acknowledges the record.
                .filter((item) => module !== "documents" || !isDocumentUnsynced(item))
                .map((item) => item[identifier_name]);

            if (identifiersToDelete.length > 0 && shouldDeleteMissingItems) {
                try {
                    await db[module].where(identifier_name).anyOf(identifiersToDelete).delete();
                } catch (deleteError) {
                    console.error(`Error deleting items from ${module}:`, deleteError);
                }
            }

            if (itemsToUpdateOrCreate.length > 0) {
                try {
                    // Ensure database is still open before writing
                    if (!db.isOpen()) {
                        await db.open();
                    }

                    if (module === "images") {
                        // For images, validate each item has the required uuid
                        const validImages = itemsToUpdateOrCreate.filter((item) => {
                            if (!item.uuid || !item.imageable_uuid) {
                                console.warn(`Skipping image with missing uuid or imageable_uuid`);
                                return false;
                            }
                            return true;
                        });

                        if (validImages.length > 0) {
                            await db[module].bulkPut(validImages);
                        }
                    } else {
                        const parsedPayload = await module_parser(module, itemsToUpdateOrCreate);

                        // Make sure parsedPayload is an array
                        if (!Array.isArray(parsedPayload)) {
                            console.error(`module_parser returned non-array for ${module}`);
                            return 0;
                        }

                        // Further validate that each item has the primary key
                        const validItems = parsedPayload.filter((item) => {
                            if (!item || typeof item !== "object") {
                                console.warn(`Invalid item in parsed payload for ${module}`);
                                return false;
                            }

                            if (item[identifier_name] === undefined || item[identifier_name] === null) {
                                console.warn(`Item missing ${identifier_name} in module ${module} after parsing`);
                                return false;
                            }

                            return true;
                        });

                        if (validItems.length > 0) {
                            await db[module].bulkPut(validItems);
                        }
                    }
                } catch (putError) {
                    console.error(`Error storing items in ${module}:`, putError);
                }
            }

            // Runs after the bulkPut above: rows overwritten by the server copy already
            // lost the flag (delete is then a no-op), this only touches rows the pull
            // kept because the local version was equal or newer.
            if (unsyncedFlagsToClear.length > 0) {
                try {
                    await db[module]
                        .where(identifier_name)
                        .anyOf(unsyncedFlagsToClear)
                        .modify((item) => {
                            delete item.not_synced;
                        });
                } catch (modifyError) {
                    console.error(`Error clearing not_synced flags in ${module}:`, modifyError);
                }
            }

            return downloadedImages;
        } catch (error) {
            console.error(`Unexpected error in add function for module ${module}:`, error);
            return 0;
        }
    }

    /**
     * Helper: Fetch a blob from a URL
     */
    async fetchBlob(url) {
        try {
            const response = await fetch(url, {
                mode: "cors",
                credentials: "omit",
            });

            if (!response.ok) {
                throw new Error(`Failed to fetch image: ${response.status}`);
            }

            return await response.blob();
        } catch (error) {
            console.error("Error fetching blob:", error);
            return null;
        }
    }

    /**
     * Helper: Convert a blob to base64
     */
    blobToBase64(blob) {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result);
            reader.onerror = reject;
            reader.readAsDataURL(blob);
        });
    }

    /**
     * Helper: Convert base64 to a File object
     */
    base64ToFile(base64Data, filename) {
        try {
            if (!base64Data) {
                throw new Error("Invalid base64 data: null or undefined");
            }

            const parts = base64Data.split(",");
            if (parts.length < 2) {
                throw new Error("Invalid base64 data format");
            }

            // Extract mime type from base64 string
            let mimeString = "image/jpeg"; // Default fallback
            const mimeMatch = parts[0].match(/data:(.*);base64/);
            if (mimeMatch && mimeMatch.length > 1) {
                mimeString = mimeMatch[1];
            }

            // Determine proper file extension based on mime type
            let extension = "jpg";
            if (mimeString === "image/png") extension = "png";
            if (mimeString === "image/gif") extension = "gif";
            if (mimeString === "image/webp") extension = "webp";

            // Create a more appropriate filename with the correct extension
            const fileNameWithExt = filename.includes(".") ? filename : `${filename}.${extension}`;

            const byteString = atob(parts[1]);
            const ab = new ArrayBuffer(byteString.length);
            const ia = new Uint8Array(ab);

            for (let i = 0; i < byteString.length; i++) {
                ia[i] = byteString.charCodeAt(i);
            }

            const blob = new Blob([ia], { type: mimeString });
            return new File([blob], fileNameWithExt, { type: mimeString });
        } catch (error) {
            console.error("Error converting base64 to file:", error);
            throw error;
        }
    }

    /**
     * Helper: Split an array into chunks
     */
    chunkArray(array, chunkSize) {
        const chunks = [];
        for (let i = 0; i < array.length; i += chunkSize) {
            chunks.push(array.slice(i, i + chunkSize));
        }
        return chunks;
    }

    /**
     * Set up task timeout
     */
    setupTaskTimeout(duration = 5 * 60 * 1000) {
        // 5 minutes default
        clearTimeout(this.taskTimeoutId);

        this.taskTimeoutId = setTimeout(() => {
            if (!this.isRunning) return;

            const inactiveTime = Math.round((Date.now() - this.lastProgressUpdate) / 1000);
            console.error(`Task ${this.currentTask?.type} appears to be stalled (${inactiveTime}s without updates)`);

            // Signal the running loops to terminate at their next check point
            if (this.currentTask?.cancelToken) {
                this.currentTask.cancelToken.cancelled = true;
            }

            // Task failed due to timeout
            this.isRunning = false;

            // Store task info before nullifying
            const taskType = this.currentTask?.type;
            const duration = this.currentTask ? Date.now() - this.currentTask.startTime : 0;

            // Reset state
            this.currentTask = null;

            // Trigger timeout callback
            this.triggerCallback("timeout", taskType, {
                inactiveSeconds: inactiveTime,
                duration: duration,
            });
        }, duration);
    }

    /**
     * Handle progress updates
     */
    handleProgress(taskType, progress, details) {
        // Reset task timeout on progress
        this.setupTaskTimeout();

        // Update timestamp for health monitoring
        this.lastProgressUpdate = Date.now();

        // Call the progress callback if registered
        this.triggerCallback("progress", taskType, { progress, details });

        // Update store based on task type
        switch (taskType) {
            case "process_queue":
                store.dispatch("setQueueProgress", progress);
                break;
            case "sync_inspection":
                store.dispatch("setInspectionSyncProgress", progress);
                break;
            case "compress_images":
                // For image compression, progress is tracked per call
                break;
        }
    }

    /**
     * Helper to call the appropriate callback if registered
     */
    triggerCallback(event, taskType, data) {
        // Task-specific callback
        if (this.callbacks[event]) {
            this.callbacks[event](data);
        }

        // Update store state based on task and event
        this.updateStoreState(taskType, event, data);
    }

    /**
     * Update Vuex store based on task events
     */
    updateStoreState(taskType, event, data) {
        switch (taskType) {
            case "process_queue":
                if (event === "start") {
                    store.dispatch("setQueue", true);
                } else if (["complete", "error", "stop", "timeout"].includes(event)) {
                    store.dispatch("setQueue", false);
                }
                break;

            case "sync_inspection":
                if (event === "start") {
                    store.dispatch("setSyncing", true);
                } else if (["complete", "error", "stop", "timeout"].includes(event)) {
                    store.dispatch("setSyncing", false);
                }

                // Special case: when sync is complete, update inspection sync time
                if (event === "complete") {
                    // Use setTimeout to ensure this runs after other updates
                    setTimeout(() => this.finalizeInspectionSync(), 500);
                }
                break;
        }
    }

    /**
     * Finalize inspection sync by updating last sync time
     */
    async finalizeInspectionSync() {
        try {
            const inspection_id = store.getters.getInspectionId;
            if (!inspection_id) {
                console.log("No inspection ID in store, skipping finalization");
                return;
            }

            const inspection = await db.inspections.get(inspection_id);

            if (inspection) {
                const inspection_sync = {
                    inspection_uuid: inspection.uuid,
                    updated_at: new Date().toISOString(),
                };

                store.dispatch("setInspectionSynced", inspection_sync);
            } else {
                console.log(`Inspection with ID ${inspection_id} not found in the database`);
            }
        } catch (error) {
            console.error("Error finalizing inspection sync:", error);
            // Don't rethrow - this shouldn't block the completion of sync
        }
    }

    /**
     * Setup base tables
     */
    async setupBaseTables(params, updateProgress, cancelToken = null) {
        const { api_url, bearer, online } = params;

        // Check network status - don't proceed if offline
        if (!online && !params.ignoreOffline) {
            throw new Error("Cannot set up base tables while offline");
        }

        if (!api_url || !bearer) {
            throw new Error("Missing API configuration for base tables setup");
        }

        updateProgress(0, "Starting base tables setup");

        try {
            // First check if database is open
            if (!db.isOpen()) {
                console.warn("Database not open for base tables setup, attempting to open");
                try {
                    await db.open();
                    console.log("Database opened for base tables setup");
                } catch (dbError) {
                    console.error("Failed to open database for base tables setup:", dbError);
                    throw new Error("Database access error: " + dbError.message);
                }
            }

            const tables = [
                "component_groups",
                "components",
                "danger_aspects",
                "error_classifications",
                "error_indications",
                "forms",
                "installation_types",
                "location_item_types",
                "measurement_types",
                "measurement_instrument_types",
                "measurement_instruments",
                "regulations",
                "remarks",
                "repair_periods",
                "location_rotationfields",
                "inspection_method_types",
                "inspection_methods",
                "inspection_statuses",
                "location_system_types",
                "document_types",
            ];

            // Fetch all setup data in one request with timeout
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), 30000); // 30 second timeout

            try {
                const response = await fetch(`${api_url}/setup`, {
                    headers: {
                        Authorization: `Bearer ${bearer}`,
                        Accept: "application/vnd.api+json",
                        ContentType: "application/vnd.api+json",
                    },
                    signal: controller.signal,
                });

                clearTimeout(timeoutId);

                if (!response.ok) {
                    throw new Error(`Setup data fetch failed: ${response.status}`);
                }

                const data = await response.json();

                // Process each table - spread work evenly across progress 0-90%
                const progressPerTable = 90 / tables.length;

                // Process tables in batches for better UI responsiveness
                const tableChunks = this.chunkArray(tables, 4);

                for (let i = 0; i < tableChunks.length; i++) {
                    // Stop writing when the task was cancelled; base tables are
                    // re-fetched in full on the next setup run.
                    if (cancelToken?.cancelled) {
                        console.log(`[setupBaseTables] Task cancelled at table batch ${i + 1}/${tableChunks.length}, stopping`);
                        return;
                    }

                    const tableChunk = tableChunks[i];

                    // Create a batch of promises for this chunk
                    const batchPromises = tableChunk.map(async (table) => {
                        const tableData = data[table];

                        if (tableData) {
                            try {
                                // Double-check database is still open
                                if (!db.isOpen()) {
                                    console.warn(`Database closed while processing table ${table}, reopening`);
                                    await db.open();
                                }

                                if (table === "components") {
                                    await db.base_components.bulkPut(tableData);
                                } else if (table === "measurement_types") {
                                    // Add (code) to the name for easier searching
                                    tableData.forEach((measurement_type) => {
                                        measurement_type.name = `${measurement_type.name} (${measurement_type.code})`;
                                    });
                                    await db.measurement_types.bulkPut(tableData);
                                } else if (table === "forms") {
                                    // db.forms is the global form catalog. The /setup catalog
                                    // rows may omit `questions`, whereas the inspection /optimized
                                    // sync stores them. A blind bulkPut would then strip questions
                                    // that an inspection needs, so preserve the local `questions`
                                    // when the incoming catalog form lacks them. Scope the read to
                                    // the ids being written. When questions are injected a new
                                    // object is created (the original /setup payload is never
                                    // mutated); otherwise the row is passed through unchanged.
                                    const incomingIds = tableData.map((f) => f.id);
                                    const localForms = await db.forms.where("id").anyOf(incomingIds).toArray();
                                    const localQuestions = new Map(
                                        localForms.map((f) => [f.id, f.questions])
                                    );
                                    const mergedForms = tableData.map((form) => {
                                        const local = localQuestions.get(form.id);
                                        return form.questions == null && local != null
                                            ? { ...form, questions: local }
                                            : form;
                                    });
                                    await db.forms.bulkPut(mergedForms);
                                } else {
                                    await db[table].bulkPut(tableData);
                                }

                                return { table, success: true };
                            } catch (tableError) {
                                console.error(`Error processing table ${table}:`, tableError);
                                return { table, success: false, error: tableError };
                            }
                        }
                        return { table, success: true, skipped: true };
                    });

                    // Wait for all tables in this chunk to process
                    const results = await Promise.all(batchPromises);

                    // Report progress
                    updateProgress(
                        progressPerTable * (i * tableChunk.length + tableChunk.length),
                        `Processed tables batch ${i + 1}/${tableChunks.length}`
                    );

                    // Yield to UI thread between chunks
                    await new Promise((resolve) => setTimeout(resolve, 0));
                }

                // Try to sync measurement type revisions
                updateProgress(90, "Syncing measurement type revisions");
                try {
                    await this.syncMeasurementTypeRevisions(api_url, bearer);
                } catch (revisionError) {
                    console.warn("Failed to sync measurement type revisions, continuing anyway:", revisionError);
                }

                updateProgress(100, "Base tables setup complete");
            } catch (fetchError) {
                clearTimeout(timeoutId);
                if (fetchError.name === "AbortError") {
                    throw new Error("Setup data fetch timed out");
                }
                throw fetchError;
            }
        } catch (error) {
            console.error("Setup error:", error);
            throw error;
        }
    }

    /**
     * Sync measurement type revisions
     */
    async syncMeasurementTypeRevisions(api_url, bearer) {
        try {
            // First check if the database is open
            if (!db.isOpen()) {
                console.warn("Database not open for measurement type revisions sync, attempting to open");
                try {
                    await db.open();
                    console.log("Database opened for measurement type revisions");
                } catch (dbError) {
                    console.error("Failed to open database for measurement type revisions:", dbError);
                    throw new Error("Database access error: " + dbError.message);
                }
            }

            // Fetch the data with timeout
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), 30000); // 30 second timeout

            const response = await fetch(`${api_url}/measurement-type-revisions`, {
                headers: {
                    Authorization: `Bearer ${bearer}`,
                    Accept: "application/vnd.api+json",
                    ContentType: "application/vnd.api+json",
                },
                signal: controller.signal,
            });

            clearTimeout(timeoutId);

            if (!response.ok) {
                throw new Error(`Failed to fetch measurement type revisions: ${response.status}`);
            }

            const data = await response.json();

            // Check if data is valid
            if (!Array.isArray(data)) {
                console.warn("Measurement type revisions data is not an array, skipping");
                return; // Skip silently rather than failing
            }

            // Try to store the data with retry logic
            let stored = false;
            for (let attempt = 0; attempt < 3 && !stored; attempt++) {
                try {
                    // Double-check database is still open
                    if (!db.isOpen()) {
                        await db.open();
                    }

                    await db.measurement_type_revisions.bulkPut(data);
                    stored = true;
                    console.log(`Stored ${data.length} measurement type revisions on attempt ${attempt + 1}`);
                } catch (storageError) {
                    console.error(`Failed to store measurement type revisions (attempt ${attempt + 1}):`, storageError);

                    // Special handling for database closed errors
                    if (
                        storageError.name === "DatabaseClosedError" ||
                        storageError.message.includes("DatabaseClosedError")
                    ) {
                        console.log("Database closed during storage, attempting to reopen");
                        try {
                            await db.open();
                        } catch (reopenError) {
                            console.error("Error reopening database:", reopenError);
                        }
                    }

                    // Add delay before retry
                    if (attempt < 2) {
                        // Don't wait after last attempt
                        await new Promise((resolve) => setTimeout(resolve, 1000 * (attempt + 1)));
                    }
                }
            }

            if (!stored) {
                // Instead of failing hard, log the error but allow the process to continue
                console.error("Failed to store measurement type revisions after multiple attempts");
            }
        } catch (error) {
            console.error("Error in syncMeasurementTypeRevisions:", error);
            // Convert to a non-fatal error so the setup can complete
            console.warn("Continuing setup process despite measurement type revisions error");
        }
    }

    /**
     * Compress and save images
     */
    async compressImages(params, updateProgress, cancelToken = null) {
        const {
            images,
            upload_url,
            imageable_uuid,
            addTimestamp = false,
            addInspectorName = false,
            inspectorName = null,
            cropToSquare = false,
        } = params;

        // Validate input
        if (!images || !Array.isArray(images) || images.length === 0) {
            throw new Error("Invalid or empty images array");
        }

        updateProgress(0, `Starting compression of ${images.length} images`);

        // Process images in smaller batches for better UI responsiveness
        const imageChunks = this.chunkArray(images, 3); // Process 3 images at a time

        for (let i = 0; i < imageChunks.length; i++) {
            // Stop between batches when cancelled — images already compressed and
            // saved keep their pending upload jobs and sync later as usual.
            if (cancelToken?.cancelled) {
                console.log(`[compressImages] Task cancelled at batch ${i + 1}/${imageChunks.length}, stopping`);
                return;
            }

            const imageChunk = imageChunks[i];

            // Process each image in this chunk sequentially (each image is heavy)
            for (let j = 0; j < imageChunk.length; j++) {
                const image = imageChunk[j];
                const imageIndex = i * 3 + j; // Calculate overall image index

                try {
                    // Validate base64 data
                    if (!image.base64 || typeof image.base64 !== "string") {
                        console.error(`Invalid base64 data for image ${imageIndex + 1}`);
                        continue;
                    }

                    updateProgress(
                        Math.round((imageIndex / images.length) * 50),
                        `Compressing image ${imageIndex + 1}/${images.length}`
                    );

                    // Compress the image (1MB target, quality 0.75)
                    // NOTE: convert.compress() ALWAYS outputs JPEG format regardless of input
                    // See convert.js documentation for rationale (backend compatibility, PDF generation, etc.)
                    const compressedBase64 = await convert.compress(
                        image.base64,
                        1, // maxSizeMB: 1MB (changed from 2MB)
                        1920, // targetWidth: max dimension
                        0.75, // minQuality: JPEG quality (changed from 0.9)
                        false, // addLabel: no label
                        cropToSquare,
                        addTimestamp,
                        addInspectorName,
                        inspectorName
                    );

                    // Create the new image object
                    // IMPORTANT: Always set format to 'image/jpeg' since convert.compress() always outputs JPEG
                    const newImage = {
                        ...image,
                        base64: compressedBase64,
                        compressed: true,
                        format: "image/jpeg", // Always JPEG after compression (was: format - bug!)
                    };

                    // Save to database with initial upload status
                    await db.images.put({
                        ...newImage,
                        upload_status: "pending",
                        sync_status: "pending",
                    });

                    // Create S3 upload job if URL is provided
                    if (upload_url) {
                        // Determine entity type from URL
                        let entityType = "observation"; // default
                        if (upload_url.includes("/measurements/")) {
                            entityType = "measurement";
                        } else if (upload_url.includes("/location-item-components/")) {
                            entityType = "location_item_component";
                        } else if (upload_url.includes("/locations/")) {
                            entityType = "location";
                        }

                        // If online (respects force offline mode), upload immediately
                        if (store.getters.online) {
                            try {
                                await imageUploadService.uploadImage(newImage, entityType, imageable_uuid);
                            } catch (error) {
                                console.error("Failed to upload image immediately:", error);
                                // Queue for later if immediate upload fails
                                const jobUuid = uuid();
                                await db.jobs.put({
                                    uuid: jobUuid,
                                    type: "image-s3-upload",
                                    module: "images",
                                    url: null,
                                    attempt: 0, // Initialize attempt counter
                                    payload: {
                                        uuid: jobUuid,
                                        image_uuid: newImage.uuid,
                                        imageable_uuid: imageable_uuid,
                                        entity_type: entityType,
                                    },
                                    created_at: new Date().toISOString(),
                                });
                            }
                        } else {
                            // Queue for later upload when offline
                            const jobUuid = uuid();
                            await db.jobs.put({
                                uuid: jobUuid,
                                type: "image-s3-upload",
                                module: "images",
                                url: null,
                                attempt: 0, // Initialize attempt counter
                                payload: {
                                    uuid: jobUuid,
                                    image_uuid: newImage.uuid,
                                    imageable_uuid: imageable_uuid,
                                    entity_type: entityType,
                                },
                                created_at: new Date().toISOString(),
                            });
                        }
                    }

                    // Update progress for second half (saving)
                    updateProgress(
                        50 + Math.round((imageIndex / images.length) * 50),
                        `Saving image ${imageIndex + 1}/${images.length}`
                    );
                } catch (error) {
                    console.error(`Error processing image ${imageIndex + 1}:`, error);
                }
            }

            // Yield to UI thread between chunks to prevent freezing
            await new Promise((resolve) => setTimeout(resolve, 10));
        }

        updateProgress(100, `Successfully processed ${images.length} images`);
    }

    /**
     * Stop the current task
     */
    stopTask(reason = "User requested stop") {
        if (!this.isRunning) {
            console.log("No task running to stop");
            return false;
        }

        // Store task info before stopping
        const taskType = this.currentTask?.type;
        const duration = this.currentTask ? Date.now() - this.currentTask.startTime : 0;

        // Signal the running loops to actually terminate at their next check point.
        // Without this, "stopping" only reset the bookkeeping while the async task
        // kept processing in the background, racing any newly started task.
        if (this.currentTask?.cancelToken) {
            this.currentTask.cancelToken.cancelled = true;
        }

        // Update state
        this.isRunning = false;
        this.currentTask = null;

        // Clear timeout
        clearTimeout(this.taskTimeoutId);

        // Trigger stop callback
        this.triggerCallback("stop", taskType, {
            reason,
            duration,
        });

        return true;
    }

    /**
     * Check status of current task
     */
    checkStatus() {
        return {
            running: this.isRunning,
            task: this.currentTask,
            lastUpdate: this.lastProgressUpdate,
            platform: this.platform,
        };
    }
}

// Export singleton instance
export default new SyncManager();
